import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { FiCrosshair, FiLogOut, FiMessageCircle, FiPhone, FiVolume2, FiVolumeX, FiX } from 'react-icons/fi'
import { getSocket } from '../store/socketClient'
import * as authAction from '../store/authAction'
import '../styles/zombie.css'

const WORLD_WIDTH = 4800
const WORLD_HEIGHT = 2400
const TAU = Math.PI * 2
const props = [
  // Haunted village
  ...[[280, 260], [560, 260], [270, 610], [620, 620], [1500, 1025], [2050, 1085], [360, 2155], [2120, 2200],
    [2605, 250], [2885, 260], [3365, 300], [3825, 250], [4145, 260], [4515, 300], [4020, 2150]]
    .map(([x, y], i) => ({ type: 'house', x, y, i })),
  // Cemetery
  ...Array.from({ length: 34 }, (_, i) => ({ type: 'grave', x: 1450 + (i % 7) * 105 + (i % 2) * 14, y: 210 + Math.floor(i / 7) * 120 })),
  // Pumpkin farm
  ...Array.from({ length: 40 }, (_, i) => ({ type: 'pumpkin', x: 190 + (i % 8) * 92, y: 1530 + Math.floor(i / 8) * 112 })),
  // Asylum
  { type: 'asylum', x: 1760, y: 1720 },
  // Forest dividers
  ...Array.from({ length: 30 }, (_, i) => ({ type: 'tree', x: 80 + i * 82, y: 1140 + Math.sin(i * 1.7) * 60 })),
  ...Array.from({ length: 18 }, (_, i) => ({ type: 'tree', x: 1160 + Math.sin(i) * 55, y: 80 + i * 132 })),
  ...[[2530,1690,1],[2780,1510,.8],[3410,1870,1.15],[2670,2240,.75],[3290,2160,.9],
    [3920,1680,1],[4310,1880,.85],[4580,2210,1.1]]
    .map(([x, y, size]) => ({ type: 'cactus', x, y, size }))
]

const places = [
  { name: 'HAUNTED VILLAGE', x: 470, y: 100 },
  { name: 'CEMETERY OF WHISPERS', x: 1800, y: 100 },
  { name: 'PUMPKIN FARM', x: 470, y: 1380 },
  { name: 'ABANDONED ASYLUM', x: 1820, y: 1380 },
  { name: "WITCH'S WOODS", x: 1180, y: 890 },
  { name: "DEAD MAN'S CROSSROADS", x: 1190, y: 1265 },
  { name: 'BLOOD MOON FAIR', x: 2050, y: 870 },
  { name: 'BLACKWATER MARSH', x: 1710, y: 2260 },
  { name: 'DREADWOOD TOWN', x: 2980, y: 85 },
  { name: 'SAINT HOLLOW HOSPITAL', x: 3100, y: 665 },
  { name: 'DEAD DUST DESERT', x: 3040, y: 1430 },
  { name: 'BONE YARD STRIP', x: 4180, y: 85 },
  { name: 'GALLOW DUNES', x: 4300, y: 1430 }
]
const streetLights = [
  [820, 1000], [1030, 1090], [1370, 1210], [1590, 1250], [1160, 430], [1195, 780], [1260, 1530], [1290, 1920],
  [2510, 540], [2860, 570], [3310, 575], [2780, 910], [3250, 1190], [3060, 1700],
  [3810, 540], [4180, 570], [4560, 575], [4120, 1190], [4380, 1700]
]
const roadCracks = [
  [420, 1080, .2], [720, 1130, -.15], [980, 1180, .35], [1430, 1270, -.2], [1810, 1320, .18],
  [1090, 260, 1.4], [1160, 690, 1.5], [1230, 1490, 1.45], [1300, 2070, 1.52],
  [2520, 1140, .15], [2910, 1160, -.22], [3320, 1190, .18], [3070, 1510, 1.5], [3050, 2140, 1.4],
  [3820, 1140, .16], [4210, 1170, -.2], [4580, 1195, .14], [4300, 1680, 1.48]
]
const buildings = [
  { id: 'village-house', name: 'The Hollow House', x: 560, y: 375 },
  { id: 'chapel', name: 'Blackwood Chapel', x: 790, y: 675 },
  { id: 'crypt', name: 'Cemetery Crypt', x: 1830, y: 650 },
  { id: 'farmhouse', name: 'Pumpkin Farmhouse', x: 650, y: 1830 },
  { id: 'asylum', name: 'Abandoned Asylum', x: 1760, y: 1870 },
  { id: 'gun-shop', name: 'Grimshot Gun Shop', x: 2730, y: 555 },
  { id: 'hospital', name: 'Saint Hollow Hospital', x: 3100, y: 1045 },
  { id: 'desert-motel', name: 'Last Light Motel', x: 3060, y: 2000 }
]
const worldStructures = [
  { kind: 'chapel', x: 685, y: 440, w: 210, h: 180, doorX: 790 },
  { kind: 'crypt', x: 1730, y: 430, w: 200, h: 170, doorX: 1830 },
  { kind: 'farm', x: 545, y: 1590, w: 210, h: 185, doorX: 650 },
  { kind: 'gunshop', x: 2625, y: 320, w: 210, h: 180, doorX: 2730 },
  { kind: 'hospital', x: 2940, y: 710, w: 320, h: 280, doorX: 3100 },
  { kind: 'motel', x: 2945, y: 1760, w: 230, h: 185, doorX: 3060 }
]
const interiorSolidRects = {
  'village-house': [
    { x: 320, y: 315, w: 260, h: 125 }, { x: 90, y: 110, w: 70, h: 250 },
    { x: 105, y: 590, w: 210, h: 105 }, { x: 625, y: 580, w: 170, h: 110 }, { x: 390, y: 660, w: 120, h: 90 }
  ],
  chapel: [
    { x: 345, y: 115, w: 210, h: 95 }, { x: 150, y: 315, w: 235, h: 52 }, { x: 515, y: 315, w: 235, h: 52 },
    { x: 150, y: 440, w: 235, h: 52 }, { x: 515, y: 440, w: 235, h: 52 },
    { x: 150, y: 565, w: 235, h: 52 }, { x: 515, y: 565, w: 235, h: 52 }
  ],
  crypt: [
    { x: 120, y: 170, w: 180, h: 85 }, { x: 600, y: 170, w: 180, h: 85 },
    { x: 120, y: 390, w: 180, h: 85 }, { x: 600, y: 390, w: 180, h: 85 },
    { x: 120, y: 610, w: 180, h: 85 }, { x: 600, y: 610, w: 180, h: 85 }, { x: 415, y: 340, w: 70, h: 180 }
  ],
  farmhouse: [
    { x: 90, y: 105, w: 80, h: 310 }, { x: 170, y: 105, w: 220, h: 75 },
    { x: 320, y: 340, w: 260, h: 130 }, { x: 650, y: 110, w: 155, h: 135 },
    { x: 630, y: 570, w: 170, h: 125 }, { x: 100, y: 590, w: 175, h: 110 }
  ],
  asylum: [
    { x: 105, y: 145, w: 190, h: 85 }, { x: 605, y: 145, w: 190, h: 85 },
    { x: 105, y: 350, w: 190, h: 85 }, { x: 605, y: 350, w: 190, h: 85 },
    { x: 105, y: 555, w: 190, h: 85 }, { x: 605, y: 555, w: 190, h: 85 }, { x: 405, y: 210, w: 90, h: 350 }
  ],
  'gun-shop': [
    { x: 95, y: 120, w: 710, h: 80 }, { x: 100, y: 300, w: 240, h: 110 },
    { x: 560, y: 300, w: 240, h: 110 }, { x: 360, y: 520, w: 180, h: 100 }
  ],
  hospital: [
    { x: 100, y: 130, w: 210, h: 85 }, { x: 590, y: 130, w: 210, h: 85 },
    { x: 100, y: 380, w: 210, h: 85 }, { x: 590, y: 380, w: 210, h: 85 },
    { x: 365, y: 250, w: 170, h: 300 }
  ],
  'desert-motel': [
    { x: 90, y: 125, w: 720, h: 85 }, { x: 110, y: 380, w: 190, h: 110 },
    { x: 610, y: 380, w: 180, h: 110 }, { x: 350, y: 600, w: 200, h: 90 }
  ]
}

const spriteSheets = {}
const spriteCache = new Map()
const weaponSpriteCache = new Map()
const weaponCrops = {
  pistol: [21, 611, 52, 37],
  shotgun: [78, 610, 68, 39],
  smg: [151, 608, 67, 41],
  rifle: [223, 608, 75, 41],
  sniper: [300, 604, 88, 45]
}
const getSpriteSheet = (kind) => {
  if (spriteSheets[kind]) return spriteSheets[kind]
  const image = new Image()
  image.src = kind === 'zombie' ? '/zombie/zombie-roster.jpg' : '/zombie/human-roster.jpg'
  spriteSheets[kind] = image
  return image
}

const transparentSprite = (kind, index) => {
  const key = `${kind}:${index}`
  if (spriteCache.has(key)) return spriteCache.get(key)
  const image = getSpriteSheet(kind)
  if (!image.complete || !image.naturalWidth) return null
  const zombie = kind === 'zombie'
  const sx = zombie ? 548 + index * 78 : (index % 10) * 102.4 + 3
  const sy = zombie ? 24 : Math.floor(index / 10) * 256 + 28
  const sw = zombie ? 74 : 96
  const sh = zombie ? 132 : 190
  const canvas = document.createElement('canvas')
  canvas.width = Math.round(sw)
  canvas.height = Math.round(sh)
  const context = canvas.getContext('2d', { willReadFrequently: true })
  context.drawImage(image, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height)
  try {
    const frame = context.getImageData(0, 0, canvas.width, canvas.height)
    const data = frame.data
    // JPEG sprite sheets have a softly shaded backdrop and some characters touch
    // a corner. The most common border colour is a safer key than averaging corners.
    const border = []
    for (let x = 0; x < canvas.width; x += 1) border.push(x, x + (canvas.height - 1) * canvas.width)
    for (let y = 1; y < canvas.height - 1; y += 1) border.push(y * canvas.width, canvas.width - 1 + y * canvas.width)
    const buckets = new Map()
    for (const pixel of border) {
      const at = pixel * 4
      const key = `${data[at] >> 3}:${data[at + 1] >> 3}:${data[at + 2] >> 3}`
      const bucket = buckets.get(key) || { count: 0, r: 0, g: 0, b: 0 }
      bucket.count += 1
      bucket.r += data[at]; bucket.g += data[at + 1]; bucket.b += data[at + 2]
      buckets.set(key, bucket)
    }
    const mode = [...buckets.values()].sort((a, b) => b.count - a.count)[0]
    const bg = [mode.r / mode.count, mode.g / mode.count, mode.b / mode.count]
    const threshold = zombie ? 68 : 56
    const seen = new Uint8Array(canvas.width * canvas.height)
    const queue = [...border]
    for (let head = 0; head < queue.length; head += 1) {
      const pixel = queue[head]
      if (seen[pixel]) continue
      seen[pixel] = 1
      const at = pixel * 4
      const diff = Math.hypot(data[at] - bg[0], data[at + 1] - bg[1], data[at + 2] - bg[2])
      if (diff > threshold) continue
      data[at + 3] = 0
      const x = pixel % canvas.width
      const y = Math.floor(pixel / canvas.width)
      if (x > 0) queue.push(pixel - 1)
      if (x + 1 < canvas.width) queue.push(pixel + 1)
      if (y > 0) queue.push(pixel - canvas.width)
      if (y + 1 < canvas.height) queue.push(pixel + canvas.width)
    }
    // Clear tiny enclosed patches of the same backdrop (between arms and legs).
    for (let pixel = 0; pixel < canvas.width * canvas.height; pixel += 1) {
      const at = pixel * 4
      if (data[at + 3] === 0) continue
      const diff = Math.hypot(data[at] - bg[0], data[at + 1] - bg[1], data[at + 2] - bg[2])
      if (diff < (zombie ? 34 : 28)) data[at + 3] = 0
    }
    context.putImageData(frame, 0, 0)
  } catch (err) {}
  spriteCache.set(key, canvas)
  return canvas
}

const weaponSprite = (kind = 'pistol') => {
  if (weaponSpriteCache.has(kind)) return weaponSpriteCache.get(kind)
  const image = getSpriteSheet('zombie')
  if (!image.complete || !image.naturalWidth) return null
  const crop = weaponCrops[kind] || weaponCrops.pistol
  const canvas = document.createElement('canvas')
  canvas.width = crop[2]
  canvas.height = crop[3]
  const context = canvas.getContext('2d', { willReadFrequently: true })
  // Five weapons cropped from the WEAPONS row in the supplied artwork.
  context.drawImage(image, ...crop, 0, 0, canvas.width, canvas.height)
  try {
    const frame = context.getImageData(0, 0, canvas.width, canvas.height)
    const data = frame.data
    const samples = [0, canvas.width - 1, canvas.width * (canvas.height - 1), canvas.width * canvas.height - 1]
    const bg = samples.reduce((color, pixel) => {
      const at = pixel * 4
      color[0] += data[at] / samples.length
      color[1] += data[at + 1] / samples.length
      color[2] += data[at + 2] / samples.length
      return color
    }, [0, 0, 0])
    const seen = new Uint8Array(canvas.width * canvas.height)
    const queue = []
    for (let x = 0; x < canvas.width; x += 1) queue.push(x, x + (canvas.height - 1) * canvas.width)
    for (let y = 0; y < canvas.height; y += 1) queue.push(y * canvas.width, canvas.width - 1 + y * canvas.width)
    for (let head = 0; head < queue.length; head += 1) {
      const pixel = queue[head]
      if (seen[pixel]) continue
      seen[pixel] = 1
      const at = pixel * 4
      if (Math.hypot(data[at] - bg[0], data[at + 1] - bg[1], data[at + 2] - bg[2]) > 65) continue
      data[at + 3] = 0
      const x = pixel % canvas.width
      const y = Math.floor(pixel / canvas.width)
      if (x) queue.push(pixel - 1)
      if (x + 1 < canvas.width) queue.push(pixel + 1)
      if (y) queue.push(pixel - canvas.width)
      if (y + 1 < canvas.height) queue.push(pixel + canvas.width)
    }
    context.putImageData(frame, 0, 0)
  } catch (err) {}
  weaponSpriteCache.set(kind, canvas)
  return canvas
}

const pickAvatar = (value, fallback = 0) => {
  const number = Number.isFinite(Number(value)) ? Math.floor(Number(value)) : fallback
  return ((number % 20) + 20) % 20
}

const avatarNumber = (id, total) => {
  let number = 0
  for (let i = 0; i < String(id).length; i += 1) number = (number * 31 + String(id).charCodeAt(i)) >>> 0
  return number % total
}

const humanColor = (id) => {
  let n = 0
  for (let i = 0; i < String(id).length; i += 1) n = (n * 31 + String(id).charCodeAt(i)) >>> 0
  return `hsl(${n % 360} 66% 60%)`
}

const fmt = (ms) => {
  const total = Math.max(0, Math.floor(ms / 1000))
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

const drawHouseDetails = (ctx, item) => {
  ctx.strokeStyle = 'rgba(8,7,6,.45)'
  ctx.lineWidth = 3
  for (let y = -43; y < 48; y += 21) {
    ctx.beginPath(); ctx.moveTo(-83, y); ctx.lineTo(83, y + ((item.i % 2) * 3)); ctx.stroke()
  }
  ctx.strokeStyle = 'rgba(120,108,82,.22)'
  ctx.lineWidth = 2
  for (let x = -86; x <= 86; x += 24) {
    ctx.beginPath(); ctx.moveTo(x, -55); ctx.lineTo(x + 5, 55); ctx.stroke()
  }
  ctx.strokeStyle = '#1b130f'
  ctx.lineWidth = 4
  ctx.strokeRect(-62, -28, 30, 30); ctx.strokeRect(34, -28, 30, 30)
  ctx.beginPath()
  ctx.moveTo(-47, -28); ctx.lineTo(-47, 2); ctx.moveTo(-62, -13); ctx.lineTo(-32, -13)
  ctx.moveTo(49, -28); ctx.lineTo(49, 2); ctx.moveTo(34, -13); ctx.lineTo(64, -13); ctx.stroke()
  ctx.fillStyle = '#1a130f'
  ctx.fillRect(-66, 2, 38, 6); ctx.fillRect(30, 2, 38, 6)
  ctx.fillStyle = item.i % 2 ? '#3a2418' : '#2a1c14'
  ctx.fillRect(-78, -30, 14, 36); ctx.fillRect(64, -30, 14, 36)
  ctx.fillStyle = '#d9a148'
  ctx.beginPath(); ctx.arc(8, 33, 2.5, 0, TAU); ctx.fill()
  ctx.fillStyle = '#c9a05a'
  ctx.beginPath(); ctx.arc(-22, 22, 3, 0, TAU); ctx.fill()
  ctx.strokeStyle = '#0d0b09'; ctx.lineWidth = 4
  ctx.beginPath()
  for (let x = -96; x <= 96; x += 18) {
    ctx.moveTo(x, -57 - (item.i === 1 ? 18 : Math.max(0, 70 - Math.abs(x) * .62)))
    ctx.lineTo(x + 12, -55)
  }
  ctx.stroke()
  if (item.i % 3 === 0) {
    ctx.strokeStyle = '#171b12'; ctx.lineWidth = 5
    ctx.beginPath(); ctx.moveTo(-82, 42); ctx.lineTo(-105, 12); ctx.moveTo(-93, 29); ctx.lineTo(-112, 34); ctx.stroke()
  }
  if (item.i % 2 === 0) {
    ctx.strokeStyle = '#24160f'; ctx.lineWidth = 3
    ctx.beginPath()
    ctx.moveTo(-58, -24); ctx.lineTo(-36, -2); ctx.moveTo(-58, -2); ctx.lineTo(-36, -24)
    ctx.stroke()
  }
  ctx.fillStyle = 'rgba(0,0,0,.35)'; ctx.fillRect(-86, 52, 172, 10)
  ctx.fillStyle = '#2a2018'; ctx.fillRect(-36, 48, 72, 10)
  ctx.strokeStyle = '#120e0b'; ctx.lineWidth = 3
  ctx.beginPath(); ctx.moveTo(-28, 58); ctx.lineTo(-28, 72); ctx.moveTo(28, 58); ctx.lineTo(28, 72); ctx.stroke()
  ctx.fillStyle = item.i % 2 ? 'rgba(232,176,72,.35)' : 'rgba(90,140,160,.22)'
  ctx.fillRect(-58, -24, 22, 22); ctx.fillRect(38, -24, 22, 22)
  if (item.i % 4 === 1) {
    ctx.fillStyle = '#6b1d1a'; ctx.fillRect(-70, 8, 18, 42)
    ctx.strokeStyle = '#c9a46a'; ctx.lineWidth = 2
    ctx.beginPath(); ctx.arc(-61, 30, 3, 0, TAU); ctx.stroke()
  }
  if (item.i % 4 === 2) {
    ctx.strokeStyle = 'rgba(70,90,48,.55)'; ctx.lineWidth = 4
    ctx.beginPath(); ctx.moveTo(78, 48); ctx.quadraticCurveTo(96, 8, 72, -38); ctx.stroke()
  }
}

const drawMapDetails = (ctx) => {
  // Grass, dirt and moonlit ground texture.
  for (let i = 0; i < 105; i += 1) {
    const x = 55 + ((i * 193) % 4690)
    const y = 70 + ((i * 317) % 2240)
    ctx.fillStyle = i % 3 ? 'rgba(87,91,48,.08)' : 'rgba(107,63,38,.07)'
    ctx.beginPath(); ctx.ellipse(x, y, 28 + (i % 5) * 9, 9 + (i % 4) * 5, (i % 7) * .3, 0, TAU); ctx.fill()
  }
  for (const [x, y, angle] of roadCracks) {
    ctx.save(); ctx.translate(x, y); ctx.rotate(angle)
    ctx.strokeStyle = 'rgba(8,8,6,.48)'; ctx.lineWidth = 4
    ctx.beginPath(); ctx.moveTo(-28, 0); ctx.lineTo(-7, -7); ctx.lineTo(5, 5); ctx.lineTo(28, -4)
    ctx.moveTo(-5, -5); ctx.lineTo(-12, -20); ctx.moveTo(8, 4); ctx.lineTo(15, 18); ctx.stroke()
    ctx.restore()
  }
  for (const [x, y] of streetLights) {
    ctx.fillStyle = '#191714'; ctx.fillRect(x - 5, y - 62, 10, 68)
    ctx.fillRect(x - 18, y - 68, 36, 8)
    ctx.shadowColor = '#e7b753'; ctx.shadowBlur = 25
    ctx.fillStyle = '#d8a84c'; ctx.beginPath(); ctx.arc(x, y - 73, 10, 0, TAU); ctx.fill()
    ctx.shadowBlur = 0
  }

  // Witch camp.
  ctx.fillStyle = '#191719'; ctx.beginPath(); ctx.ellipse(1010, 815, 39, 22, 0, 0, TAU); ctx.fill()
  ctx.strokeStyle = '#443729'; ctx.lineWidth = 8
  ctx.beginPath(); ctx.moveTo(978, 802); ctx.lineTo(994, 772); ctx.moveTo(1042, 802); ctx.lineTo(1026, 772); ctx.stroke()
  ctx.fillStyle = '#5cad43'; ctx.shadowColor = '#80e85e'; ctx.shadowBlur = 18
  ctx.beginPath(); ctx.ellipse(1010, 801, 29, 8, 0, 0, TAU); ctx.fill(); ctx.shadowBlur = 0

  // Blood Moon fair.
  ctx.strokeStyle = '#4f3b2d'; ctx.lineWidth = 8
  ctx.beginPath(); ctx.arc(2110, 790, 68, 0, TAU); ctx.stroke()
  for (let i = 0; i < 8; i += 1) {
    const a = i * TAU / 8
    ctx.beginPath(); ctx.moveTo(2110, 790); ctx.lineTo(2110 + Math.cos(a) * 68, 790 + Math.sin(a) * 68); ctx.stroke()
  }
  ctx.fillStyle = '#251416'; ctx.beginPath(); ctx.moveTo(1900, 835); ctx.lineTo(1960, 765); ctx.lineTo(2020, 835); ctx.closePath(); ctx.fill()
  ctx.fillStyle = '#6c2428'; ctx.fillRect(1905, 835, 110, 55)

  // Blackwater pools, reeds and a broken walkway.
  for (const [x, y, rx, ry] of [[1580,2180,130,42],[1810,2290,180,48],[1450,2320,90,30]]) {
    ctx.fillStyle = 'rgba(25,57,57,.48)'; ctx.beginPath(); ctx.ellipse(x, y, rx, ry, -.08, 0, TAU); ctx.fill()
    ctx.strokeStyle = 'rgba(93,114,83,.28)'; ctx.lineWidth = 3; ctx.stroke()
  }
  ctx.strokeStyle = '#3a2a1e'; ctx.lineWidth = 16
  ctx.beginPath(); ctx.moveTo(1480, 2215); ctx.lineTo(1710, 2260); ctx.stroke()
  ctx.strokeStyle = '#17120e'; ctx.lineWidth = 3
  for (let x = 1490; x < 1700; x += 28) {
    ctx.beginPath(); ctx.moveTo(x, 2210 + (x - 1480) * .2); ctx.lineTo(x - 2, 2232 + (x - 1480) * .2); ctx.stroke()
  }

  // Dreadwood town square: cracked cobbles, gallows and Halloween lanterns.
  ctx.fillStyle = 'rgba(67,65,56,.3)'; ctx.fillRect(2460, 390, 1040, 230)
  ctx.strokeStyle = 'rgba(15,15,13,.38)'; ctx.lineWidth = 3
  for (let x = 2480; x < 3490; x += 58) {
    ctx.beginPath(); ctx.moveTo(x, 395); ctx.lineTo(x - 22, 615); ctx.stroke()
  }
  for (let y = 420; y < 620; y += 45) {
    ctx.beginPath(); ctx.moveTo(2460, y); ctx.lineTo(3500, y); ctx.stroke()
  }
  ctx.strokeStyle = '#251b14'; ctx.lineWidth = 15
  ctx.beginPath(); ctx.moveTo(3440, 560); ctx.lineTo(3440, 430); ctx.lineTo(3365, 430); ctx.stroke()
  ctx.strokeStyle = '#55402c'; ctx.lineWidth = 4
  ctx.beginPath(); ctx.moveTo(3365, 430); ctx.lineTo(3365, 478); ctx.stroke()
  for (const [x, y] of [[2520,430],[2890,440],[3320,500]]) {
    ctx.fillStyle = '#bd4d12'; ctx.beginPath(); ctx.ellipse(x, y, 20, 16, 0, 0, TAU); ctx.fill()
    ctx.fillStyle = '#ffd066'; ctx.beginPath(); ctx.arc(x - 6, y - 3, 3, 0, TAU); ctx.arc(x + 6, y - 3, 3, 0, TAU); ctx.fill()
  }

  // Hospital grounds: ambulance wreck, warning markings and sickly fog.
  ctx.fillStyle = 'rgba(144,164,151,.07)'; ctx.beginPath(); ctx.ellipse(3100, 1110, 430, 105, 0, 0, TAU); ctx.fill()
  ctx.fillStyle = '#d7d9d3'; ctx.fillRect(3330, 930, 155, 72)
  ctx.fillStyle = '#7b1719'; ctx.fillRect(3393, 943, 28, 45); ctx.fillRect(3385, 952, 45, 27)
  ctx.fillStyle = '#151718'; ctx.beginPath(); ctx.arc(3360, 1005, 17, 0, TAU); ctx.arc(3450, 1005, 17, 0, TAU); ctx.fill()
  ctx.strokeStyle = '#9b2624'; ctx.lineWidth = 8
  ctx.beginPath(); ctx.moveTo(2690, 1260); ctx.lineTo(3470, 1295); ctx.stroke()

  // Dead Dust desert: dunes, cacti, bones and a crooked welcome sign.
  for (const [x, y, rx] of [[2580,1510,170],[3050,1455,210],[3400,1660,145],[2750,2190,230],[3340,2260,190]]) {
    ctx.fillStyle = 'rgba(154,99,44,.16)'; ctx.beginPath(); ctx.ellipse(x, y, rx, 42, -.08, Math.PI, TAU); ctx.fill()
  }
  ctx.strokeStyle = '#6b5b43'; ctx.lineWidth = 8
  ctx.beginPath(); ctx.moveTo(2860, 1600); ctx.lineTo(2915, 1620); ctx.moveTo(2887, 1610); ctx.lineTo(2872, 1640); ctx.stroke()
  ctx.fillStyle = '#4a301d'; ctx.fillRect(2445, 1435, 190, 55)
  ctx.fillStyle = '#d1a45c'; ctx.font = '700 20px Georgia'; ctx.textAlign = 'center'; ctx.fillText('NO WAY BACK', 2540, 1470)

  ctx.fillStyle = 'rgba(154,99,44,.14)'
  for (const [x, y, rx] of [[3920,1510,150],[4380,1480,190],[4680,1760,120],[4100,2240,210]]) {
    ctx.beginPath(); ctx.ellipse(x, y, rx, 36, -.06, Math.PI, TAU); ctx.fill()
  }
  ctx.fillStyle = '#3a2418'; ctx.fillRect(4280, 380, 220, 90)
  ctx.fillStyle = '#7a251d'; ctx.fillRect(4270, 352, 240, 32)
  ctx.fillStyle = '#e8c36a'; ctx.font = '700 18px Georgia'; ctx.fillText('BONE YARD DINER', 4390, 374)
  ctx.fillStyle = '#1a120e'; ctx.fillRect(4310, 430, 28, 40); ctx.fillRect(4440, 430, 28, 40)
}

const drawMap = (ctx) => {
  ctx.fillStyle = '#10140f'
  ctx.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT)
  const grad = ctx.createRadialGradient(1800, 1200, 20, 1800, 1200, 2200)
  grad.addColorStop(0, 'rgba(71,58,30,.12)')
  grad.addColorStop(1, 'rgba(0,0,0,.55)')
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT)
  ctx.fillStyle = 'rgba(91,61,31,.32)'
  ctx.fillRect(2400, 1360, 2400, 1040)
  ctx.fillStyle = 'rgba(42,43,38,.28)'
  ctx.fillRect(2400, 45, 2400, 650)
  ctx.fillStyle = 'rgba(35,42,40,.28)'
  ctx.fillRect(2400, 650, 2400, 710)

  // Roads
  ctx.strokeStyle = '#151511'
  ctx.lineWidth = 126
  ctx.lineCap = 'round'
  ctx.beginPath()
  ctx.moveTo(120, 1050); ctx.lineTo(4680, 1190)
  ctx.moveTo(1050, 80); ctx.lineTo(1320, 2320)
  ctx.moveTo(2670, 80); ctx.lineTo(2840, 700); ctx.lineTo(3150, 1120); ctx.lineTo(3060, 2320)
  ctx.moveTo(3480, 1120); ctx.lineTo(4680, 1180)
  ctx.moveTo(4180, 90); ctx.lineTo(4320, 2320)
  ctx.stroke()
  ctx.strokeStyle = '#302c23'
  ctx.lineWidth = 102
  ctx.stroke()
  ctx.strokeStyle = 'rgba(117,102,72,.13)'
  ctx.lineWidth = 78
  ctx.stroke()
  ctx.strokeStyle = 'rgba(190,153,83,.08)'
  ctx.lineWidth = 3
  ctx.setLineDash([16, 24])
  ctx.stroke()
  ctx.setLineDash([])
  drawMapDetails(ctx)

  for (const place of places) {
    ctx.font = '700 24px Georgia'
    ctx.textAlign = 'center'
    ctx.fillStyle = 'rgba(229,205,151,.35)'
    ctx.fillText(place.name, place.x, place.y)
  }

  for (const item of props) {
    ctx.save()
    ctx.translate(item.x, item.y)
    if (item.type === 'house') {
      const walls = ['#30281f', '#26302b', '#332226', '#2b292f'][item.i % 4]
      const roof = ['#15110e', '#131918', '#1a0e10', '#18151c'][item.i % 4]
      ctx.fillStyle = 'rgba(0,0,0,.28)'; ctx.beginPath(); ctx.ellipse(8, 64, 112, 24, 0, 0, TAU); ctx.fill()
      ctx.fillStyle = walls; ctx.fillRect(-86, -58, 172, 116)
      ctx.fillStyle = roof
      if (item.i === 1) {
        ctx.fillRect(-105, -77, 210, 28)
        ctx.fillStyle = walls; ctx.fillRect(-58, -106, 116, 55)
      } else {
        ctx.beginPath(); ctx.moveTo(-105, -55); ctx.lineTo(item.i === 2 ? -25 : 0, -125); ctx.lineTo(105, -55); ctx.closePath(); ctx.fill()
      }
      if (item.i === 0 || item.i === 3) {
        ctx.fillStyle = '#201713'; ctx.fillRect(52, -118, 23, 63)
        ctx.fillStyle = 'rgba(130,130,120,.15)'; ctx.beginPath(); ctx.arc(64, -139, 20, 0, TAU); ctx.fill()
      }
      ctx.fillStyle = item.i === 2 ? '#3b1716' : '#70421f'; ctx.fillRect(-15, 8, 30, 50)
      ctx.fillStyle = item.i % 2 ? '#d07d28' : '#67523a'
      ctx.fillRect(-62, -28, 30, 30); ctx.fillRect(34, -28, 30, 30)
      ctx.strokeStyle = '#1d1712'; ctx.lineWidth = 5
      if (item.i === 2) {
        ctx.beginPath(); ctx.moveTo(-66, -15); ctx.lineTo(-30, -24); ctx.moveTo(30, -25); ctx.lineTo(67, -8); ctx.stroke()
      }
      if (item.i === 3) {
        ctx.fillStyle = '#302117'; ctx.fillRect(-45, 48, 90, 13)
        ctx.strokeStyle = '#19110d'; ctx.beginPath(); ctx.moveTo(-38, 58); ctx.lineTo(-38, 76); ctx.moveTo(38, 58); ctx.lineTo(38, 76); ctx.stroke()
      }
      drawHouseDetails(ctx, item)
    } else if (item.type === 'grave') {
      ctx.fillStyle = '#444842'
      ctx.beginPath(); ctx.arc(0, -18, 17, Math.PI, 0); ctx.lineTo(17, 29); ctx.lineTo(-17, 29); ctx.closePath(); ctx.fill()
      ctx.strokeStyle = '#282b27'; ctx.lineWidth = 4
      ctx.beginPath(); ctx.moveTo(0, -23); ctx.lineTo(0, 12); ctx.moveTo(-10, -8); ctx.lineTo(10, -8); ctx.stroke()
      ctx.strokeStyle = '#5d615a'; ctx.lineWidth = 2
      ctx.beginPath(); ctx.moveTo(-8, 4); ctx.lineTo(2, 13); ctx.lineTo(-4, 25); ctx.stroke()
    } else if (item.type === 'pumpkin') {
      ctx.fillStyle = '#b84b12'
      ctx.beginPath(); ctx.ellipse(0, 0, 25, 20, 0, 0, TAU); ctx.fill()
      ctx.strokeStyle = '#6f2d0d'; ctx.lineWidth = 3
      ctx.beginPath(); ctx.moveTo(0, -18); ctx.lineTo(0, 18); ctx.stroke()
      ctx.fillStyle = '#2a160c'
      ctx.beginPath(); ctx.moveTo(-13, -6); ctx.lineTo(-5, -11); ctx.lineTo(-4, -2); ctx.closePath()
      ctx.moveTo(13, -6); ctx.lineTo(5, -11); ctx.lineTo(4, -2); ctx.closePath(); ctx.fill()
      ctx.fillStyle = '#4a5e21'; ctx.fillRect(-3, -28, 6, 11)
    } else if (item.type === 'tree') {
      ctx.fillStyle = 'rgba(0,0,0,.25)'; ctx.beginPath(); ctx.ellipse(9, 43, 32, 11, 0, 0, TAU); ctx.fill()
      ctx.fillStyle = '#18130e'; ctx.fillRect(-9, -2, 18, 49)
      ctx.strokeStyle = '#0c100c'; ctx.lineWidth = 12; ctx.lineCap = 'round'
      ctx.beginPath(); ctx.moveTo(0, 12); ctx.lineTo(-33, -40); ctx.lineTo(-54, -52)
      ctx.moveTo(-25, -28); ctx.lineTo(-12, -60)
      ctx.moveTo(0, -5); ctx.lineTo(31, -58); ctx.lineTo(52, -66)
      ctx.moveTo(25, -47); ctx.lineTo(19, -76)
      ctx.moveTo(0, -18); ctx.lineTo(-4, -84); ctx.lineTo(-20, -102); ctx.stroke()
      ctx.strokeStyle = '#211a12'; ctx.lineWidth = 5
      ctx.beginPath(); ctx.moveTo(-6, 42); ctx.lineTo(-29, 55); ctx.moveTo(7, 42); ctx.lineTo(32, 54); ctx.stroke()
    } else if (item.type === 'cactus') {
      ctx.scale(item.size || 1, item.size || 1)
      ctx.fillStyle = 'rgba(0,0,0,.22)'; ctx.beginPath(); ctx.ellipse(8, 48, 35, 10, 0, 0, TAU); ctx.fill()
      ctx.strokeStyle = '#26391e'; ctx.lineWidth = 18; ctx.lineCap = 'round'
      ctx.beginPath(); ctx.moveTo(0, 45); ctx.lineTo(0, -52); ctx.moveTo(0, -5); ctx.lineTo(-28, -24); ctx.lineTo(-28, -43)
      ctx.moveTo(0, 12); ctx.lineTo(30, -8); ctx.lineTo(30, -30); ctx.stroke()
      ctx.strokeStyle = '#53633a'; ctx.lineWidth = 3
      for (let y = -42; y < 40; y += 18) { ctx.beginPath(); ctx.moveTo(-8, y); ctx.lineTo(8, y - 4); ctx.stroke() }
    } else if (item.type === 'asylum') {
      ctx.fillStyle = '#25272a'; ctx.fillRect(-270, -150, 540, 300)
      ctx.fillStyle = '#17191b'
      ctx.beginPath(); ctx.moveTo(-300, -150); ctx.lineTo(0, -250); ctx.lineTo(300, -150); ctx.closePath(); ctx.fill()
      ctx.fillStyle = '#101113'; ctx.fillRect(-40, 50, 80, 100)
      for (let x = -210; x <= 210; x += 105) {
        ctx.fillStyle = '#5e4827'; ctx.fillRect(x - 23, -90, 46, 55)
      }
      ctx.fillStyle = '#5b5e61'; ctx.font = '700 30px Georgia'; ctx.textAlign = 'center'; ctx.fillText('ASYLUM', 0, -174)
      ctx.strokeStyle = 'rgba(10,12,12,.4)'; ctx.lineWidth = 3
      for (let y = -140; y < 140; y += 28) { ctx.beginPath(); ctx.moveTo(-260, y); ctx.lineTo(260, y); ctx.stroke() }
      ctx.fillStyle = 'rgba(200,170,90,.2)'
      for (let x = -210; x <= 210; x += 105) ctx.fillRect(x - 18, -82, 36, 22)
    }
    ctx.restore()
  }
  // Enterable structures that are also solid on the server.
  for (const item of worldStructures) {
    drawStructureObject(ctx, item)
  }
  ctx.strokeStyle = '#321b1b'
  ctx.lineWidth = 35
  ctx.strokeRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT)
}

const drawStructureObject = (ctx, item) => {
  const color = item.kind === 'crypt' ? '#3a3d3b'
    : item.kind === 'farm' ? '#3c2b1d'
      : item.kind === 'gunshop' ? '#4b3025'
        : item.kind === 'hospital' ? '#59615e'
          : item.kind === 'motel' ? '#60452c' : '#29251f'
  ctx.fillStyle = color
  ctx.fillRect(item.x, item.y, item.w, item.h)
  if (item.kind === 'hospital') {
    ctx.fillStyle = '#252a29'; ctx.fillRect(item.x - 12, item.y - 30, item.w + 24, 34)
    ctx.fillStyle = '#822927'; ctx.fillRect(item.x + item.w / 2 - 14, item.y + 25, 28, 70)
    ctx.fillRect(item.x + item.w / 2 - 38, item.y + 46, 76, 28)
    for (let x = item.x + 30; x < item.x + item.w - 20; x += 62) {
      ctx.fillStyle = '#1a2322'; ctx.fillRect(x, item.y + 125, 38, 48)
      ctx.strokeStyle = '#829084'; ctx.lineWidth = 4; ctx.strokeRect(x, item.y + 125, 38, 48)
    }
    ctx.fillStyle = '#c0b9a5'; ctx.font = '700 20px Georgia'; ctx.textAlign = 'center'
    ctx.fillText('SAINT HOLLOW', item.x + item.w / 2, item.y - 8)
  } else {
    ctx.fillStyle = item.kind === 'farm' ? '#24120e' : item.kind === 'motel' ? '#301b14' : '#131411'
    if (item.kind === 'gunshop' || item.kind === 'motel') {
      ctx.fillRect(item.x - 14, item.y - 38, item.w + 28, 42)
    } else {
      ctx.beginPath(); ctx.moveTo(item.x - 18, item.y); ctx.lineTo(item.x + item.w / 2, item.y - 75)
      ctx.lineTo(item.x + item.w + 18, item.y); ctx.closePath(); ctx.fill()
    }
  }
  ctx.fillStyle = '#17120e'; ctx.fillRect(item.doorX - 18, item.y + item.h - 55, 36, 55)
  if (item.kind === 'chapel') {
    ctx.fillStyle = '#83532c'; ctx.fillRect(item.x + 25, item.y + 45, 35, 60); ctx.fillRect(item.x + 150, item.y + 45, 35, 60)
    ctx.strokeStyle = '#76643e'; ctx.lineWidth = 10
    ctx.beginPath(); ctx.moveTo(item.doorX, item.y - 68); ctx.lineTo(item.doorX, item.y - 112)
    ctx.moveTo(item.doorX - 20, item.y - 92); ctx.lineTo(item.doorX + 20, item.y - 92); ctx.stroke()
  } else if (item.kind === 'crypt') {
    ctx.strokeStyle = '#202321'; ctx.lineWidth = 8
    ctx.strokeRect(item.x + 30, item.y + 35, 45, 60); ctx.strokeRect(item.x + 125, item.y + 35, 45, 60)
  } else if (item.kind === 'gunshop') {
    ctx.fillStyle = '#bd4a29'; ctx.fillRect(item.x + 16, item.y - 31, item.w - 32, 25)
    ctx.fillStyle = '#f0d18b'; ctx.font = '700 18px Georgia'; ctx.textAlign = 'center'
    ctx.fillText('GRIMSHOT GUNS', item.x + item.w / 2, item.y - 12)
    for (const x of [item.x + 35, item.x + 142]) {
      ctx.fillStyle = '#242927'; ctx.fillRect(x, item.y + 45, 34, 60)
      ctx.strokeStyle = '#a13d28'; ctx.lineWidth = 5; ctx.strokeRect(x, item.y + 45, 34, 60)
    }
  } else if (item.kind === 'motel') {
    ctx.fillStyle = '#7f251f'; ctx.fillRect(item.x + 16, item.y - 31, item.w - 32, 25)
    ctx.fillStyle = '#e9bb65'; ctx.font = '700 17px Georgia'; ctx.textAlign = 'center'
    ctx.fillText('LAST LIGHT MOTEL', item.x + item.w / 2, item.y - 12)
    for (let x = item.x + 20; x < item.x + item.w - 20; x += 55) {
      ctx.fillStyle = '#1e1510'; ctx.fillRect(x, item.y + 45, 35, 68)
      ctx.fillStyle = '#bb5b24'; ctx.fillRect(x + 28, item.y + 77, 3, 3)
    }
  } else if (item.kind !== 'hospital') {
    ctx.fillStyle = '#ab6b26'; ctx.fillRect(item.x + 30, item.y + 50, 38, 38); ctx.fillRect(item.x + item.w - 68, item.y + 50, 38, 38)
    if (item.kind === 'farm') { ctx.fillStyle = '#17100b'; ctx.fillRect(item.x + item.w - 50, item.y - 55, 24, 62) }
  }
  ctx.fillStyle = 'rgba(0,0,0,.28)'; ctx.fillRect(item.x - 8, item.y + item.h, item.w + 16, 12)
  ctx.fillStyle = '#17120e'; ctx.fillRect(item.doorX - 26, item.y + item.h - 8, 52, 10)
  if (item.kind === 'hospital') {
    ctx.fillStyle = 'rgba(180,210,200,.12)'
    for (let x = item.x + 30; x < item.x + item.w - 20; x += 62) ctx.fillRect(x + 4, item.y + 129, 30, 20)
    ctx.strokeStyle = 'rgba(8,10,10,.35)'; ctx.lineWidth = 2
    for (let y = item.y + 18; y < item.y + item.h - 10; y += 22) {
      ctx.beginPath(); ctx.moveTo(item.x + 8, y); ctx.lineTo(item.x + item.w - 8, y); ctx.stroke()
    }
  }
  if (item.kind === 'gunshop') {
    ctx.fillStyle = 'rgba(220,70,40,.18)'; ctx.fillRect(item.x + 8, item.y + 18, item.w - 16, 18)
    ctx.fillStyle = '#d7b56a'; ctx.font = '700 11px sans-serif'; ctx.textAlign = 'center'
    ctx.fillText('OPEN · 1 SHOT LEFT', item.x + item.w / 2, item.y + 31)
  }
  if (item.kind === 'motel') {
    ctx.fillStyle = '#2a1a12'; ctx.fillRect(item.x - 18, item.y + 20, 16, 90)
    ctx.fillStyle = '#c9a05a'; ctx.fillRect(item.x - 28, item.y + 18, 36, 10)
  }
  if (item.kind === 'chapel') {
    ctx.fillStyle = 'rgba(180,140,70,.16)'
    ctx.fillRect(item.x + 25, item.y + 45, 35, 60); ctx.fillRect(item.x + 150, item.y + 45, 35, 60)
    ctx.fillStyle = '#2a2118'; ctx.fillRect(item.doorX - 40, item.y + item.h - 18, 80, 14)
    ctx.fillStyle = '#c9a05a'; ctx.beginPath(); ctx.arc(item.doorX + 28, item.y + item.h - 62, 4, 0, TAU); ctx.fill()
  }
  if (item.kind === 'crypt') {
    ctx.fillStyle = '#2a2d2b'; ctx.fillRect(item.x - 10, item.y + item.h - 16, item.w + 20, 16)
    ctx.strokeStyle = 'rgba(180,190,170,.18)'; ctx.lineWidth = 3
    ctx.beginPath(); ctx.moveTo(item.x + 38, item.y + 48); ctx.lineTo(item.x + 67, item.y + 82)
    ctx.moveTo(item.x + 133, item.y + 48); ctx.lineTo(item.x + 162, item.y + 82); ctx.stroke()
  }
  if (item.kind === 'farm') {
    ctx.fillStyle = '#3a2a1c'; ctx.fillRect(item.x - 18, item.y + 70, 18, 90)
    ctx.fillStyle = '#6b4a28'; ctx.fillRect(item.x + 18, item.y + item.h - 22, item.w - 36, 12)
    ctx.strokeStyle = '#24160f'; ctx.lineWidth = 3
    for (let x = item.x + 28; x < item.x + item.w - 20; x += 22) {
      ctx.beginPath(); ctx.moveTo(x, item.y + item.h - 22); ctx.lineTo(x, item.y + item.h - 6); ctx.stroke()
    }
  }
  ctx.strokeStyle = 'rgba(10,8,6,.28)'; ctx.lineWidth = 2
  for (let y = item.y + 12; y < item.y + item.h - 8; y += 16) {
    ctx.beginPath(); ctx.moveTo(item.x + 6, y); ctx.lineTo(item.x + item.w - 6, y + (item.kind === 'motel' ? 3 : 0)); ctx.stroke()
  }
}

let mapCache = null
const cachedMap = () => {
  if (mapCache) return mapCache
  mapCache = document.createElement('canvas')
  mapCache.width = WORLD_WIDTH
  mapCache.height = WORLD_HEIGHT
  drawMap(mapCache.getContext('2d'))
  return mapCache
}

// Redraw only an object's pixels in front of a character. Never copy a
// rectangular section of the map here or the ground becomes a visible box.
const drawForegroundProp = (ctx, item) => {
  ctx.save()
  ctx.translate(item.x, item.y)
  if (item.type === 'tree') {
    ctx.fillStyle = 'rgba(0,0,0,.25)'; ctx.beginPath(); ctx.ellipse(9, 43, 32, 11, 0, 0, TAU); ctx.fill()
    ctx.fillStyle = '#18130e'; ctx.fillRect(-9, -2, 18, 49)
    ctx.strokeStyle = '#0c100c'; ctx.lineWidth = 12; ctx.lineCap = 'round'
    ctx.beginPath(); ctx.moveTo(0, 12); ctx.lineTo(-33, -40); ctx.lineTo(-54, -52)
    ctx.moveTo(-25, -28); ctx.lineTo(-12, -60)
    ctx.moveTo(0, -5); ctx.lineTo(31, -58); ctx.lineTo(52, -66)
    ctx.moveTo(25, -47); ctx.lineTo(19, -76)
    ctx.moveTo(0, -18); ctx.lineTo(-4, -84); ctx.lineTo(-20, -102); ctx.stroke()
    ctx.strokeStyle = '#211a12'; ctx.lineWidth = 5
    ctx.beginPath(); ctx.moveTo(-6, 42); ctx.lineTo(-29, 55); ctx.moveTo(7, 42); ctx.lineTo(32, 54); ctx.stroke()
  } else if (item.type === 'grave') {
    ctx.fillStyle = '#444842'
    ctx.beginPath(); ctx.arc(0, -18, 17, Math.PI, 0); ctx.lineTo(17, 29); ctx.lineTo(-17, 29); ctx.closePath(); ctx.fill()
    ctx.strokeStyle = '#282b27'; ctx.lineWidth = 4
    ctx.beginPath(); ctx.moveTo(0, -23); ctx.lineTo(0, 12); ctx.moveTo(-10, -8); ctx.lineTo(10, -8); ctx.stroke()
    ctx.strokeStyle = '#5d615a'; ctx.lineWidth = 2
    ctx.beginPath(); ctx.moveTo(-8, 4); ctx.lineTo(2, 13); ctx.lineTo(-4, 25); ctx.stroke()
  } else if (item.type === 'pumpkin') {
    ctx.fillStyle = '#b84b12'; ctx.beginPath(); ctx.ellipse(0, 0, 25, 20, 0, 0, TAU); ctx.fill()
    ctx.strokeStyle = '#6f2d0d'; ctx.lineWidth = 3
    ctx.beginPath(); ctx.moveTo(0, -18); ctx.lineTo(0, 18); ctx.stroke()
    ctx.fillStyle = '#2a160c'
    ctx.beginPath(); ctx.moveTo(-13, -6); ctx.lineTo(-5, -11); ctx.lineTo(-4, -2); ctx.closePath()
    ctx.moveTo(13, -6); ctx.lineTo(5, -11); ctx.lineTo(4, -2); ctx.closePath(); ctx.fill()
    ctx.fillStyle = '#4a5e21'; ctx.fillRect(-3, -28, 6, 11)
  } else if (item.type === 'house') {
    const walls = ['#30281f', '#26302b', '#332226', '#2b292f'][item.i % 4]
    const roof = ['#15110e', '#131918', '#1a0e10', '#18151c'][item.i % 4]
    ctx.fillStyle = 'rgba(0,0,0,.28)'; ctx.beginPath(); ctx.ellipse(8, 64, 112, 24, 0, 0, TAU); ctx.fill()
    ctx.fillStyle = walls; ctx.fillRect(-86, -58, 172, 116)
    ctx.fillStyle = roof
    if (item.i === 1) {
      ctx.fillRect(-105, -77, 210, 28); ctx.fillStyle = walls; ctx.fillRect(-58, -106, 116, 55)
    } else {
      ctx.beginPath(); ctx.moveTo(-105, -55); ctx.lineTo(item.i === 2 ? -25 : 0, -125); ctx.lineTo(105, -55); ctx.closePath(); ctx.fill()
    }
    if (item.i === 0 || item.i === 3) {
      ctx.fillStyle = '#201713'; ctx.fillRect(52, -118, 23, 63)
    }
    ctx.fillStyle = item.i === 2 ? '#3b1716' : '#70421f'; ctx.fillRect(-15, 8, 30, 50)
    ctx.fillStyle = item.i % 2 ? '#d07d28' : '#67523a'; ctx.fillRect(-62, -28, 30, 30); ctx.fillRect(34, -28, 30, 30)
    drawHouseDetails(ctx, item)
  } else if (item.type === 'cactus') {
    ctx.scale(item.size || 1, item.size || 1)
    ctx.fillStyle = 'rgba(0,0,0,.22)'; ctx.beginPath(); ctx.ellipse(8, 48, 35, 10, 0, 0, TAU); ctx.fill()
    ctx.strokeStyle = '#26391e'; ctx.lineWidth = 18; ctx.lineCap = 'round'
    ctx.beginPath(); ctx.moveTo(0, 45); ctx.lineTo(0, -52); ctx.moveTo(0, -5); ctx.lineTo(-28, -24); ctx.lineTo(-28, -43)
    ctx.moveTo(0, 12); ctx.lineTo(30, -8); ctx.lineTo(30, -30); ctx.stroke()
    ctx.strokeStyle = '#53633a'; ctx.lineWidth = 3
    for (let y = -42; y < 40; y += 18) { ctx.beginPath(); ctx.moveTo(-8, y); ctx.lineTo(8, y - 4); ctx.stroke() }
  } else if (item.type === 'asylum') {
    ctx.fillStyle = '#25272a'; ctx.fillRect(-270, -150, 540, 300)
    ctx.fillStyle = '#17191b'; ctx.beginPath(); ctx.moveTo(-300, -150); ctx.lineTo(0, -250); ctx.lineTo(300, -150); ctx.closePath(); ctx.fill()
    ctx.fillStyle = '#101113'; ctx.fillRect(-40, 50, 80, 100)
    for (let x = -210; x <= 210; x += 105) {
      ctx.fillStyle = '#5e4827'; ctx.fillRect(x - 23, -90, 46, 55)
    }
    ctx.fillStyle = '#5b5e61'; ctx.font = '700 30px Georgia'; ctx.textAlign = 'center'; ctx.fillText('ASYLUM', 0, -174)
  }
  ctx.restore()
}

const drawForegroundStructure = (ctx, item) => {
  drawStructureObject(ctx, item)
}

const drawWorldOcclusion = (ctx, player) => {
  if (!player || player.place) return
  for (const item of props) {
    const region = item.type === 'tree' ? { x: item.x - 62, y: item.y - 112, w: 124, base: item.y + 48 }
      : item.type === 'grave' ? { x: item.x - 24, y: item.y - 45, w: 48, base: item.y + 30 }
        : item.type === 'pumpkin' ? { x: item.x - 30, y: item.y - 32, w: 60, base: item.y + 21 }
          : item.type === 'cactus' ? { x: item.x - 48, y: item.y - 75, w: 96, base: item.y + 50 }
            : item.type === 'house' ? { x: item.x - 112, y: item.y - 132, w: 224, base: item.y + 60 }
              : { x: item.x - 310, y: item.y - 260, w: 620, base: item.y + 150 }
    if (player.x < region.x - 18 || player.x > region.x + region.w + 18) continue
    if (player.y >= region.base || player.y < region.y - 20) continue
    drawForegroundProp(ctx, item)
  }
  for (const item of worldStructures) {
    const region = { x: item.x - 20, y: item.y - 120, w: item.w + 40, base: item.y + item.h }
    if (player.x < region.x - 18 || player.x > region.x + region.w + 18) continue
    if (player.y >= region.base || player.y < region.y - 20) continue
    drawForegroundStructure(ctx, item)
  }
}

const drawInteriorOcclusion = (ctx, building, player) => {
  if (!building || !player) return
  for (const rect of interiorSolidRects[building.id] || []) {
    if (player.x < rect.x - 20 || player.x > rect.x + rect.w + 20) continue
    if (player.y >= rect.y + rect.h || player.y < rect.y - 75) continue
    ctx.save()
    ctx.beginPath()
    ctx.rect(rect.x, rect.y, rect.w, rect.h)
    ctx.clip()
    drawInterior(ctx, building)
    ctx.restore()
  }
}

const drawWalkingSprite = (ctx, sprite, zombie, moving, phase) => {
  const width = zombie ? 68 : 62
  const height = 88
  const left = -width / 2
  const top = -61
  if (!moving) {
    const breathing = Math.sin(phase * 0.35) * 0.7
    ctx.drawImage(sprite, left, top + breathing, width, height - breathing)
    return
  }

  const sourceHip = Math.round(sprite.height * 0.62)
  const sourceHalf = Math.round(sprite.width / 2)
  const hipY = top + height * 0.62
  const upperHeight = hipY - top + 3
  const stride = Math.sin(phase) * 0.19

  // Torso and each half of the lower body are rendered separately so the
  // character takes visible alternating steps instead of sliding over the map.
  ctx.drawImage(sprite, 0, 0, sprite.width, sourceHip + 4, left, top, width, upperHeight)
  for (let leg = 0; leg < 2; leg += 1) {
    const direction = leg === 0 ? 1 : -1
    ctx.save()
    ctx.translate((leg === 0 ? -1 : 1) * width * 0.245, hipY)
    ctx.rotate(stride * direction)
    ctx.drawImage(
      sprite,
      leg * sourceHalf,
      sourceHip,
      leg === 0 ? sourceHalf + 1 : sprite.width - sourceHalf,
      sprite.height - sourceHip,
      -width * 0.26,
      -2,
      width * 0.52,
      height * 0.4
    )
    ctx.restore()
  }
}

const drawWorldAnimations = (ctx, now) => {
  const seconds = now / 1000
  for (let i = 0; i < 20; i += 1) {
    const x = 80 + ((i * 379 + seconds * (8 + i % 4)) % 4640)
    const y = 120 + ((i * 211 + Math.sin(seconds * .7 + i) * 35) % 2140)
    const glow = .25 + Math.sin(seconds * 2.4 + i) * .2
    ctx.fillStyle = `rgba(214,184,92,${Math.max(.08, glow)})`
    ctx.shadowColor = '#dfc066'; ctx.shadowBlur = 9
    ctx.beginPath(); ctx.arc(x, y, 2.2, 0, TAU); ctx.fill()
  }
  ctx.shadowBlur = 0
  for (let i = 0; i < 7; i += 1) {
    const x = ((seconds * (18 + i * 3) + i * 440) % 5200) - 200
    const y = 300 + i * 290 + Math.sin(seconds * .22 + i) * 35
    ctx.fillStyle = 'rgba(151,166,147,.025)'
    ctx.beginPath(); ctx.ellipse(x, y, 170, 32, .08, 0, TAU); ctx.fill()
  }
  ctx.fillStyle = '#79d552'; ctx.shadowColor = '#65da43'; ctx.shadowBlur = 22
  for (let i = 0; i < 3; i += 1) {
    const phase = (seconds * .65 + i * .31) % 1
    ctx.beginPath(); ctx.arc(997 + i * 13, 804 - phase * 38, 3 + phase * 2, 0, TAU); ctx.fill()
  }
  ctx.shadowBlur = 0
  ctx.fillStyle = 'rgba(8,7,8,.8)'
  for (let i = 0; i < 5; i += 1) {
    const x = 1500 + ((seconds * 45 + i * 175) % 850)
    const y = 700 + Math.sin(seconds * 2 + i) * 45
    ctx.save(); ctx.translate(x, y); ctx.rotate(Math.sin(seconds * 8 + i) * .25)
    ctx.beginPath(); ctx.moveTo(0, 0); ctx.quadraticCurveTo(-13, -10, -25, 1); ctx.quadraticCurveTo(-10, -2, 0, 7)
    ctx.quadraticCurveTo(10, -2, 25, 1); ctx.quadraticCurveTo(13, -10, 0, 0); ctx.fill(); ctx.restore()
  }
}

const drawCombatEffects = (ctx, effects, now, place) => {
  effects.shots = effects.shots.filter((effect) => now - effect.receivedAt < 520)
  effects.bites = effects.bites.filter((effect) => now - effect.receivedAt < 700)
  effects.turns = effects.turns.filter((effect) => now - effect.receivedAt < 1100)
  for (const effect of effects.shots) {
    if ((effect.place || '') !== (place || '')) continue
    const age = now - effect.receivedAt
    const alpha = Math.max(0, 1 - age / 520)
    const spread = effect.type === 'shotgun' ? [-.13, 0, .13] : [0]
    for (const angle of spread) {
      const c = Math.cos(angle); const s = Math.sin(angle)
      const dx = effect.dx * c - effect.dy * s
      const dy = effect.dx * s + effect.dy * c
      ctx.strokeStyle = `rgba(255,220,112,${alpha * .8})`; ctx.lineWidth = effect.type === 'sniper' ? 4 : 2
      ctx.shadowColor = '#ffc64e'; ctx.shadowBlur = 14 * alpha
      ctx.beginPath(); ctx.moveTo(effect.x + dx * 24, effect.y + dy * 24)
      ctx.lineTo(effect.x + dx * effect.range, effect.y + dy * effect.range); ctx.stroke()
    }
    ctx.fillStyle = `rgba(255,238,139,${alpha})`
    ctx.shadowColor = '#ffb52e'; ctx.shadowBlur = 28
    ctx.beginPath(); ctx.arc(effect.x + effect.dx * 30, effect.y + effect.dy * 30, 5 + alpha * 8, 0, TAU); ctx.fill()
    for (let spark = 0; spark < 5; spark += 1) {
      const angle = (spark - 2) * .23
      const c = Math.cos(angle); const s = Math.sin(angle)
      const dx = effect.dx * c - effect.dy * s
      const dy = effect.dx * s + effect.dy * c
      ctx.beginPath(); ctx.arc(effect.x + dx * (35 + spark * 4), effect.y + dy * (35 + spark * 4), 2, 0, TAU); ctx.fill()
    }
    ctx.shadowBlur = 0
  }
  for (const effect of effects.bites) {
    if ((effect.place || '') !== (place || '')) continue
    const age = now - effect.receivedAt
    const pulse = age / 700
    ctx.strokeStyle = `rgba(205,38,31,${1 - pulse})`; ctx.lineWidth = 7 * (1 - pulse) + 1
    ctx.beginPath(); ctx.arc(effect.x, effect.y, 24 + pulse * 52, 0, TAU); ctx.stroke()
    ctx.fillStyle = `rgba(229,60,42,${(1 - pulse) * .6})`; ctx.font = '700 18px Creepster, serif'; ctx.textAlign = 'center'
    ctx.fillText('CHOMP!', effect.x, effect.y - 35 - pulse * 18)
  }
  for (const effect of effects.turns) {
    if ((effect.place || '') !== (place || '')) continue
    const pulse = (now - effect.receivedAt) / 1100
    ctx.strokeStyle = `rgba(105,220,73,${1 - pulse})`; ctx.lineWidth = 9
    ctx.beginPath(); ctx.arc(effect.x, effect.y, 20 + pulse * 85, 0, TAU); ctx.stroke()
  }
}

const createZombieAudio = () => {
  const AudioClass = window.AudioContext || window.webkitAudioContext
  if (!AudioClass) return null
  const context = new AudioClass()
  const master = context.createGain()
  master.gain.value = .3
  master.connect(context.destination)
  const music = context.createGain()
  music.gain.value = .1
  music.connect(master)
  const drones = [43.65, 65.41].map((frequency, index) => {
    const oscillator = context.createOscillator()
    const gain = context.createGain()
    oscillator.type = index ? 'triangle' : 'sine'
    oscillator.frequency.value = frequency
    gain.gain.value = index ? .025 : .045
    oscillator.connect(gain); gain.connect(music); oscillator.start()
    return oscillator
  })
  const lfo = context.createOscillator()
  const lfoGain = context.createGain()
  lfo.frequency.value = .11; lfoGain.gain.value = 7
  lfo.connect(lfoGain); lfoGain.connect(drones[1].frequency); lfo.start()
  const tone = (frequency, duration, type = 'sine', volume = .12, endFrequency = frequency) => {
    if (context.state === 'suspended') context.resume()
    const oscillator = context.createOscillator()
    const gain = context.createGain()
    const startAt = context.currentTime
    oscillator.type = type; oscillator.frequency.setValueAtTime(frequency, startAt)
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, endFrequency), startAt + duration)
    gain.gain.setValueAtTime(volume, startAt); gain.gain.exponentialRampToValueAtTime(.001, startAt + duration)
    oscillator.connect(gain); gain.connect(master); oscillator.start(startAt); oscillator.stop(startAt + duration)
  }
  const noise = (duration, volume = .15) => {
    const buffer = context.createBuffer(1, Math.ceil(context.sampleRate * duration), context.sampleRate)
    const channel = buffer.getChannelData(0)
    for (let i = 0; i < channel.length; i += 1) channel[i] = (Math.random() * 2 - 1) * (1 - i / channel.length)
    const source = context.createBufferSource()
    const gain = context.createGain()
    source.buffer = buffer; gain.gain.value = volume
    source.connect(gain); gain.connect(master); source.start()
  }
  const melody = window.setInterval(() => {
    if (context.state === 'running') tone([130.81, 146.83, 174.61, 98][Math.floor(Math.random() * 4)], 2.4, 'sine', .025, 65)
  }, 4200)
  return {
    play (kind, detail = '', volume = 1) {
      if (kind === 'bite') { noise(.18, .19 * volume); tone(115, .34, 'sawtooth', .12 * volume, 37) }
      if (kind === 'turn') { tone(82, .9, 'sawtooth', .11 * volume, 28); tone(160, .7, 'square', .035 * volume, 45) }
      if (kind === 'shot') {
        const pitch = detail === 'sniper' ? 62 : detail === 'shotgun' ? 78 : detail === 'smg' ? 150 : 105
        noise(detail === 'shotgun' ? .28 : .16, .2 * volume); tone(pitch, .22, 'square', .13 * volume, 34)
      }
      if (kind === 'pickup') { tone(330, .18, 'sine', .09 * volume, 520); tone(520, .3, 'triangle', .07 * volume, 760) }
      if (kind === 'door') { tone(54, .65, 'sawtooth', .1 * volume, 31); noise(.35, .06 * volume) }
    },
    setMuted (muted) { master.gain.setTargetAtTime(muted ? 0 : .3, context.currentTime, .04) },
    stop () {
      window.clearInterval(melody)
      try { drones.forEach((oscillator) => oscillator.stop()); lfo.stop(); context.close() } catch (err) {}
    }
  }
}

const drawPlayer = (ctx, p, mine, now, combat) => {
  ctx.save()
  ctx.translate(p.x, p.y)
  const recentShot = combat && [...combat.shots].reverse().find((effect) =>
    effect.playerId === p.id && now - effect.receivedAt < 190
  )
  if (recentShot) {
    const kick = (1 - (now - recentShot.receivedAt) / 190) * 9
    ctx.translate(-recentShot.dx * kick, -recentShot.dy * kick)
    ctx.rotate(Math.sin((now - recentShot.receivedAt) / 25) * .025)
  }
  const zombie = p.role === 'zombie'
  const turning = p.role === 'turning'
  const moving = Math.hypot(Number(p.dx) || 0, Number(p.dy) || 0) > 0.08
  const bob = moving ? Math.sin(now / 85 + avatarNumber(p.id, 10)) * 2.2 : 0
  const kind = zombie ? 'zombie' : 'human'
  const sheet = getSpriteSheet(kind)
  if (mine || turning || zombie) {
    ctx.shadowColor = zombie ? '#72ff63' : turning ? '#e85b42' : '#f3d17e'
    ctx.shadowBlur = mine ? 23 : 13
    ctx.fillStyle = zombie ? 'rgba(79,145,62,.3)' : turning ? 'rgba(189,54,41,.3)' : 'rgba(244,212,126,.2)'
    ctx.beginPath(); ctx.ellipse(0, 18, 34, 18, 0, 0, TAU); ctx.fill()
    ctx.shadowBlur = 0
  }
  if (sheet.complete && sheet.naturalWidth) {
    const index = zombie ? avatarNumber(p.id, 6) : pickAvatar(p.avatar, avatarNumber(p.id, 20))
    const sprite = transparentSprite(kind, index)
    const phase = now / (zombie ? 115 : 95) + avatarNumber(p.id, 17)
    ctx.save()
    ctx.translate(0, bob)
    if (moving) {
      const squash = Math.sin(phase * 2) * .025
      ctx.scale(1 + squash, 1 - squash)
    }
    if ((Number(p.dx) || 0) < -0.02) ctx.scale(-1, 1)
    if (turning) {
      ctx.filter = `sepia(1) hue-rotate(${Math.sin(now / 120) * 16 - 18}deg) saturate(1.8)`
      ctx.rotate(Math.sin(now / 85) * 0.035)
    }
    if (sprite) drawWalkingSprite(ctx, sprite, zombie, moving, phase)
    ctx.restore()
  } else {
    ctx.fillStyle = zombie ? '#4a8d42' : turning ? '#ad5a45' : humanColor(p.id)
    ctx.beginPath(); ctx.arc(0, 0, zombie ? 21 : 18, 0, TAU); ctx.fill()
    ctx.fillStyle = zombie ? '#e9ff62' : '#171614'
    ctx.beginPath(); ctx.arc(-6, -4, 3, 0, TAU); ctx.arc(6, -4, 3, 0, TAU); ctx.fill()
  }
  if (p.hasGun) {
    const gunImage = weaponSprite(p.gunType)
    ctx.save()
    if ((Number(p.dx) || 0) < -0.02) ctx.scale(-1, 1)
    ctx.rotate(-.08)
    if (gunImage) {
      ctx.drawImage(gunImage, 9, -10, 43, 30)
    } else {
      ctx.fillStyle = '#aeb4bc'; ctx.fillRect(13, -4, 30, 9)
      ctx.fillStyle = '#3c3029'; ctx.beginPath(); ctx.moveTo(20, 4); ctx.lineTo(31, 4); ctx.lineTo(27, 19); ctx.lineTo(17, 17); ctx.closePath(); ctx.fill()
    }
    ctx.restore()
  }
  if (p.stunnedUntil > now) {
    ctx.fillStyle = '#f0c84a'; ctx.font = '20px sans-serif'; ctx.textAlign = 'center'
    ctx.fillText('✦ ✦', 0, -31)
  }
  if (turning) {
    const left = Math.max(0, Math.ceil((p.turningAt - now) / 1000))
    ctx.strokeStyle = '#e14335'; ctx.lineWidth = 4
    ctx.beginPath(); ctx.arc(0, 0, 27, -Math.PI / 2, -Math.PI / 2 + TAU * (left / 10)); ctx.stroke()
    ctx.fillStyle = '#ff9a79'; ctx.font = '700 13px sans-serif'; ctx.textAlign = 'center'; ctx.fillText(left, 0, -34)
  }
  ctx.fillStyle = mine ? '#fff1bd' : 'rgba(255,255,255,.72)'
  ctx.font = `${mine ? 700 : 500} 12px sans-serif`
  ctx.textAlign = 'center'
  ctx.fillText(p.name, 0, 39)
  ctx.restore()
}

const drawInterior = (ctx, building) => {
  const id = building && building.id
  const stone = id === 'crypt' || id === 'chapel' || id === 'asylum' || id === 'hospital'
  ctx.fillStyle = stone ? '#0d0f10' : '#12110f'
  ctx.fillRect(0, 0, 900, 900)
  ctx.fillStyle = stone ? '#202224' : id === 'farmhouse' ? '#272016' : '#201b16'
  ctx.fillRect(45, 45, 810, 810)
  ctx.strokeStyle = '#090909'
  ctx.lineWidth = 35
  ctx.strokeRect(45, 45, 810, 810)
  ctx.strokeStyle = stone ? 'rgba(190,194,187,.08)' : 'rgba(167,128,73,.12)'
  ctx.lineWidth = 3
  for (let y = 80; y < 850; y += 45) {
    ctx.beginPath(); ctx.moveTo(62, y); ctx.lineTo(838, y); ctx.stroke()
  }
  if (stone) {
    for (let x = 80; x < 850; x += 90) {
      ctx.beginPath(); ctx.moveTo(x, 62); ctx.lineTo(x + ((x / 90) % 2) * 20, 838); ctx.stroke()
    }
    ctx.strokeStyle = 'rgba(3,3,3,.32)'; ctx.lineWidth = 5
    for (const [x, y] of [[220,260],[710,285],[330,720],[690,680]]) {
      ctx.beginPath(); ctx.moveTo(x - 30, y - 12); ctx.lineTo(x, y); ctx.lineTo(x - 12, y + 28); ctx.lineTo(x + 24, y + 42); ctx.stroke()
    }
  } else {
    ctx.strokeStyle = 'rgba(8,6,4,.28)'; ctx.lineWidth = 4
    for (let x = 72; x < 850; x += 38) {
      ctx.beginPath(); ctx.moveTo(x, 62); ctx.lineTo(x, 838); ctx.stroke()
    }
  }
  const rugColor = id === 'chapel' ? '#4d1720' : id === 'asylum' ? '#2c3430' : id === 'crypt' ? '#292b2b' : '#503522'
  ctx.fillStyle = rugColor
  ctx.globalAlpha = .72
  ctx.fillRect(355, 235, 190, 380)
  ctx.strokeStyle = id === 'chapel' ? '#9b743d' : '#766044'; ctx.lineWidth = 8
  ctx.strokeRect(355, 235, 190, 380)
  ctx.globalAlpha = 1
  for (const x of [215, 685]) {
    ctx.fillStyle = '#121718'; ctx.fillRect(x - 54, 68, 108, 54)
    ctx.fillStyle = 'rgba(119,158,166,.24)'; ctx.fillRect(x - 45, 76, 90, 37)
    ctx.strokeStyle = '#090b0c'; ctx.lineWidth = 6
    ctx.beginPath(); ctx.moveTo(x, 74); ctx.lineTo(x, 115); ctx.moveTo(x - 45, 94); ctx.lineTo(x + 45, 94); ctx.stroke()
  }
  for (const x of [90, 810]) {
    ctx.fillStyle = '#d9a64c'; ctx.shadowColor = '#ffb43f'; ctx.shadowBlur = 22
    ctx.beginPath(); ctx.arc(x, 270, 7, 0, TAU); ctx.fill(); ctx.shadowBlur = 0
    ctx.fillStyle = '#463522'; ctx.fillRect(x - 3, 277, 6, 31)
  }

  if (id === 'chapel') {
    // Altar, stained glass, candles and six solid pews.
    ctx.fillStyle = '#3d3329'; ctx.fillRect(345, 115, 210, 95)
    ctx.fillStyle = '#9a7b3f'; ctx.fillRect(435, 125, 30, 60)
    ctx.fillStyle = '#5b1f28'
    ctx.beginPath(); ctx.arc(450, 82, 26, 0, TAU); ctx.fill()
    for (const [x, y] of [[150,315],[515,315],[150,440],[515,440],[150,565],[515,565]]) {
      ctx.fillStyle = '#49321f'; ctx.fillRect(x, y, 235, 52)
      ctx.strokeStyle = '#24170f'; ctx.lineWidth = 7; ctx.strokeRect(x, y, 235, 52)
    }
    for (const x of [325, 575]) {
      ctx.fillStyle = '#e0af57'; ctx.fillRect(x, 175, 5, 22)
      ctx.shadowColor = '#ffb63b'; ctx.shadowBlur = 18
      ctx.beginPath(); ctx.arc(x + 2, 168, 6, 0, TAU); ctx.fill(); ctx.shadowBlur = 0
    }
    ctx.fillStyle = 'rgba(120,20,20,.18)'; ctx.beginPath(); ctx.ellipse(450, 250, 90, 18, 0, 0, TAU); ctx.fill()
    ctx.fillStyle = '#2a2118'; ctx.fillRect(418, 210, 64, 28)
    ctx.fillStyle = '#4a1d22'; ctx.beginPath(); ctx.moveTo(200, 68); ctx.lineTo(250, 118); ctx.lineTo(150, 118); ctx.closePath(); ctx.fill()
    ctx.fillStyle = '#2f4a3a'; ctx.beginPath(); ctx.moveTo(700, 68); ctx.lineTo(750, 118); ctx.lineTo(650, 118); ctx.closePath(); ctx.fill()
    ctx.fillStyle = '#d7b56a'; ctx.font = '700 16px Georgia'; ctx.textAlign = 'center'; ctx.fillText('REQUIESCAT', 450, 148)
  } else if (id === 'crypt') {
    // Six stone tombs and a central pillar.
    for (const [x, y] of [[120,170],[600,170],[120,390],[600,390],[120,610],[600,610]]) {
      ctx.fillStyle = '#45494a'; ctx.fillRect(x, y, 180, 85)
      ctx.strokeStyle = '#17191a'; ctx.lineWidth = 8; ctx.strokeRect(x, y, 180, 85)
      ctx.strokeStyle = '#252829'; ctx.lineWidth = 6
      ctx.beginPath(); ctx.moveTo(x + 90, y + 18); ctx.lineTo(x + 90, y + 65); ctx.moveTo(x + 68, y + 36); ctx.lineTo(x + 112, y + 36); ctx.stroke()
    }
    ctx.fillStyle = '#303435'; ctx.fillRect(415, 340, 70, 180)
    ctx.fillStyle = '#79705a'; ctx.font = '25px serif'
    for (const [x, y] of [[350,180],[530,260],[360,590],[545,650]]) ctx.fillText('☠', x, y)
  } else if (id === 'farmhouse') {
    // Kitchen counters, dining table, stove, pantry and hay beds.
    ctx.fillStyle = '#51402c'; ctx.fillRect(90, 105, 80, 310); ctx.fillRect(170, 105, 220, 75)
    ctx.fillStyle = '#181918'; ctx.fillRect(650, 110, 155, 135)
    ctx.fillStyle = '#7b3b21'; ctx.beginPath(); ctx.arc(725, 165, 35, 0, TAU); ctx.fill()
    ctx.fillStyle = '#5a3c23'; ctx.fillRect(320, 340, 260, 130)
    ctx.strokeStyle = '#2a1b10'; ctx.lineWidth = 9; ctx.strokeRect(320, 340, 260, 130)
    ctx.fillStyle = '#8d702d'; ctx.fillRect(630, 570, 170, 125); ctx.fillRect(100, 590, 175, 110)
    ctx.strokeStyle = '#b28b35'; ctx.lineWidth = 5
    for (let y = 590; y < 700; y += 20) { ctx.beginPath(); ctx.moveTo(100, y); ctx.lineTo(275, y - 25); ctx.stroke() }
  } else if (id === 'asylum') {
    // Rusted hospital beds, privacy wall and abandoned medicine carts.
    for (const [x, y] of [[105,145],[605,145],[105,350],[605,350],[105,555],[605,555]]) {
      ctx.fillStyle = '#34383a'; ctx.fillRect(x, y, 190, 85)
      ctx.strokeStyle = '#777b78'; ctx.lineWidth = 6; ctx.strokeRect(x, y, 190, 85)
      ctx.fillStyle = '#6d675c'; ctx.fillRect(x + 12, y + 12, 65, 30)
      ctx.fillStyle = 'rgba(116,20,16,.45)'; ctx.beginPath(); ctx.ellipse(x + 125, y + 48, 35, 15, .2, 0, TAU); ctx.fill()
    }
    ctx.fillStyle = '#323638'; ctx.fillRect(405, 210, 90, 350)
    ctx.fillStyle = '#5e6a61'; ctx.fillRect(418, 240, 64, 44); ctx.fillRect(418, 320, 64, 44)
    ctx.fillStyle = '#1c1f20'; ctx.font = '700 13px sans-serif'; ctx.textAlign = 'center'
    ctx.fillText('WARD B', 450, 198)
    ctx.strokeStyle = 'rgba(90,20,18,.55)'; ctx.lineWidth = 4
    ctx.beginPath(); ctx.moveTo(130, 240); ctx.lineTo(250, 210); ctx.moveTo(680, 430); ctx.lineTo(760, 480); ctx.stroke()
  } else if (id === 'gun-shop') {
    // Barricaded counter, empty weapon racks, display cases and a locked safe.
    ctx.fillStyle = '#4a2e21'; ctx.fillRect(95, 120, 710, 80)
    ctx.strokeStyle = '#21130e'; ctx.lineWidth = 9; ctx.strokeRect(95, 120, 710, 80)
    for (let x = 145; x < 780; x += 120) {
      ctx.strokeStyle = '#a98a56'; ctx.lineWidth = 7
      ctx.beginPath(); ctx.moveTo(x, 142); ctx.lineTo(x + 58, 170); ctx.moveTo(x + 58, 142); ctx.lineTo(x, 170); ctx.stroke()
    }
    for (const [x, y] of [[100,300],[560,300]]) {
      ctx.fillStyle = '#252a28'; ctx.fillRect(x, y, 240, 110)
      ctx.strokeStyle = '#878c80'; ctx.lineWidth = 7; ctx.strokeRect(x, y, 240, 110)
      ctx.fillStyle = 'rgba(130,176,174,.15)'; ctx.fillRect(x + 10, y + 10, 220, 55)
    }
    ctx.fillStyle = '#3a3d3b'; ctx.fillRect(360, 520, 180, 100)
    ctx.fillStyle = '#8c2d27'; ctx.beginPath(); ctx.arc(450, 570, 25, 0, TAU); ctx.fill()
    ctx.fillStyle = '#d5bd7f'; ctx.font = '700 23px Georgia'; ctx.textAlign = 'center'; ctx.fillText('TAKE ONE. MAKE IT COUNT.', 450, 255)
  } else if (id === 'hospital') {
    // Haunted treatment beds, bloody nurses' station and medicine cabinets.
    for (const [x, y] of [[100,130],[590,130],[100,380],[590,380]]) {
      ctx.fillStyle = '#d0d2ca'; ctx.fillRect(x, y, 210, 85)
      ctx.strokeStyle = '#6c7470'; ctx.lineWidth = 7; ctx.strokeRect(x, y, 210, 85)
      ctx.fillStyle = '#7d1518'; ctx.beginPath(); ctx.ellipse(x + 142, y + 48, 31, 13, .15, 0, TAU); ctx.fill()
    }
    ctx.fillStyle = '#444b48'; ctx.fillRect(365, 250, 170, 300)
    ctx.strokeStyle = '#909991'; ctx.lineWidth = 6; ctx.strokeRect(365, 250, 170, 300)
    for (let y = 290; y < 530; y += 55) {
      ctx.fillStyle = '#242927'; ctx.fillRect(385, y, 130, 35)
    }
    ctx.fillStyle = '#aa2b2a'; ctx.font = '700 42px sans-serif'; ctx.textAlign = 'center'; ctx.fillText('+', 450, 185)
    ctx.fillStyle = 'rgba(140,20,18,.35)'; ctx.beginPath(); ctx.ellipse(450, 620, 110, 22, 0, 0, TAU); ctx.fill()
    ctx.fillStyle = '#2a3330'; ctx.fillRect(80, 620, 90, 70); ctx.fillRect(730, 620, 90, 70)
    ctx.strokeStyle = '#7a8a82'; ctx.lineWidth = 4; ctx.strokeRect(80, 620, 90, 70); ctx.strokeRect(730, 620, 90, 70)
  } else if (id === 'desert-motel') {
    // Dusty reception desk, two ruined beds and luggage barricade.
    ctx.fillStyle = '#593920'; ctx.fillRect(90, 125, 720, 85)
    ctx.strokeStyle = '#29190f'; ctx.lineWidth = 9; ctx.strokeRect(90, 125, 720, 85)
    for (let x = 130; x < 790; x += 95) {
      ctx.fillStyle = '#a15524'; ctx.fillRect(x, 145, 42, 26)
      ctx.fillStyle = '#1e1510'; ctx.font = '700 13px sans-serif'; ctx.textAlign = 'center'; ctx.fillText(String(Math.floor((x - 35) / 95)), x + 21, 164)
    }
    for (const [x, y, w] of [[110,380,190],[610,380,180]]) {
      ctx.fillStyle = '#6a4a2f'; ctx.fillRect(x, y, w, 110)
      ctx.fillStyle = '#c9a36b'; ctx.fillRect(x + 14, y + 13, 72, 34)
      ctx.fillStyle = 'rgba(91,22,17,.5)'; ctx.beginPath(); ctx.ellipse(x + w - 54, y + 60, 32, 14, 0, 0, TAU); ctx.fill()
    }
    ctx.fillStyle = '#50301c'; ctx.fillRect(350, 600, 200, 90)
    ctx.strokeStyle = '#29170d'; ctx.lineWidth = 6
    ctx.beginPath(); ctx.moveTo(350, 600); ctx.lineTo(550, 690); ctx.moveTo(550, 600); ctx.lineTo(350, 690); ctx.stroke()
  } else {
    // Hollow House: fireplace, dining table, couch, bookcase and crates.
    ctx.fillStyle = '#40251c'; ctx.fillRect(90, 110, 70, 250)
    ctx.fillStyle = '#b34822'; ctx.shadowColor = '#e66a2d'; ctx.shadowBlur = 20
    ctx.beginPath(); ctx.moveTo(107, 330); ctx.lineTo(125, 285); ctx.lineTo(144, 330); ctx.fill(); ctx.shadowBlur = 0
    ctx.fillStyle = '#49331f'; ctx.fillRect(320, 315, 260, 125)
    ctx.strokeStyle = '#26180f'; ctx.lineWidth = 10; ctx.strokeRect(320, 315, 260, 125)
    ctx.fillStyle = '#343126'; ctx.fillRect(105, 590, 210, 105)
    ctx.fillStyle = '#45392a'; ctx.fillRect(625, 580, 170, 110)
    ctx.fillStyle = '#65401f'; ctx.fillRect(390, 660, 120, 90)
    ctx.strokeStyle = '#2d1b10'; ctx.lineWidth = 5
    ctx.beginPath(); ctx.moveTo(390, 660); ctx.lineTo(510, 750); ctx.moveTo(510, 660); ctx.lineTo(390, 750); ctx.stroke()
    ctx.fillStyle = '#2c241b'; ctx.fillRect(180, 430, 70, 90); ctx.fillRect(250, 455, 55, 65)
    ctx.fillStyle = '#c9a56a'; ctx.beginPath(); ctx.arc(168, 250, 8, 0, TAU); ctx.fill()
  }
  // Exit door
  ctx.fillStyle = '#090a09'; ctx.fillRect(390, 800, 120, 60)
  ctx.fillStyle = '#b08e4c'; ctx.font = '700 16px Georgia'; ctx.textAlign = 'center'; ctx.fillText('EXIT', 450, 838)
  ctx.fillStyle = 'rgba(220,205,168,.18)'; ctx.font = '700 25px Georgia'
  ctx.fillText(((building && building.name) || 'SAFE HOUSE').toUpperCase(), 450, 92)
}

const Zombie = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const canvasRef = useRef(null)
  const stateRef = useRef(null)
  const keysRef = useRef(new Set())
  const aimRef = useRef({ x: 1, y: 0 })
  const aimOriginRef = useRef({ x: 0, y: 0 })
  const joyRef = useRef({ x: 0, y: 0 })
  const joyPointerRef = useRef(null)
  const frameRef = useRef(0)
  const renderPlayersRef = useRef(new Map())
  const stateReceivedRef = useRef(performance.now())
  const combatRef = useRef({ shots: [], bites: [], turns: [] })
  const audioRef = useRef(null)
  const hadGunRef = useRef(false)
  const [game, setGame] = useState(null)
  const [playerId, setPlayerId] = useState('')
  const [screen, setScreen] = useState('intro')
  const [error, setError] = useState('')
  const [clock, setClock] = useState(Date.now())
  const [joystick, setJoystick] = useState({ active: false, dx: 0, dy: 0 })
  const [locationNotice, setLocationNotice] = useState('')
  const [chatText, setChatText] = useState('')
  const [chatOpen, setChatOpen] = useState(false)
  const [muted, setMuted] = useState(false)
  const [guestName, setGuestName] = useState('')
  const [guestAvatar, setGuestAvatar] = useState(0)
  const [phoneOpen, setPhoneOpen] = useState(false)
  const [radioTab, setRadioTab] = useState('all')
  const [radioText, setRadioText] = useState('')
  const [roomName, setRoomName] = useState('')
  const lastPlaceRef = useRef('')
  const currentPlace = (game && game.players.find((p) => p.id === playerId)?.place) || ''

  stateRef.current = game

  useEffect(() => {
    const raw = localStorage.getItem('userdata')
    if (!raw) return
    try {
      const saved = JSON.parse(raw)
      dispatch(authAction.auth(saved.id, saved.pid))
    } catch (err) {}
  }, [dispatch])

  useEffect(() => {
    const timer = setInterval(() => setClock(Date.now()), 250)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    const next = currentPlace
    if (next === lastPlaceRef.current) return undefined
    lastPlaceRef.current = next
    setChatOpen(false)
    const building = buildings.find((item) => item.id === next)
    setLocationNotice(building ? `You are inside ${building.name}` : next === '' ? 'You are back outside' : '')
    const timer = window.setTimeout(() => setLocationNotice(''), 2800)
    return () => window.clearTimeout(timer)
  }, [currentPlace])

  const guestProfile = () => {
    try { return JSON.parse(localStorage.getItem('pinweelo-zombie-guest-profile') || '{}') } catch (err) { return {} }
  }

  const openFullscreen = () => {
    const node = document.documentElement
    const request = node.requestFullscreen || node.webkitRequestFullscreen
    if (request && window.matchMedia('(max-width: 900px)').matches) {
      request.call(node).catch(() => {})
    }
  }

  const beginMatch = (saved) => {
    if (!audioRef.current) audioRef.current = createZombieAudio()
    openFullscreen()
    setError('')
    setScreen('queue')
    const socket = getSocket()
    socket.emit('con', String(saved.id))
    let settled = false
    const timeout = window.setTimeout(() => {
      if (settled) return
      settled = true
      setError('The game server did not answer. Try Start again.')
      setScreen('intro')
    }, 25000)
    socket.emit('zombie:join', { userid: saved.id, pid: saved.pid, name: saved.name, avatar: saved.avatar }, (reply) => {
      if (settled) return
      settled = true
      window.clearTimeout(timeout)
      if (!reply || !reply.ok) {
        setError((reply && reply.error) || 'Could not enter the match')
        setScreen('intro')
        return
      }
      setPlayerId(saved.id)
      stateReceivedRef.current = performance.now()
      setGame(reply.state)
      setScreen('game')
    })
  }

  const start = () => {
    const raw = localStorage.getItem('userdata')
    let saved = {}
    try { saved = JSON.parse(raw || '{}') } catch (err) {}
    if (saved.id && saved.pid) {
      beginMatch({ id: saved.id, pid: saved.pid, name: saved.name, avatar: pickAvatar(saved.avatar) })
      return
    }
    const profile = guestProfile()
    let guestId = localStorage.getItem('pinweelo-zombie-guest')
    if (!/^guest-[a-z0-9-]{8,80}$/i.test(String(guestId || ''))) {
      const random = window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`
      guestId = `guest-${random}`
      localStorage.setItem('pinweelo-zombie-guest', guestId)
    }
    if (profile.name) {
      beginMatch({ id: guestId, pid: '', name: profile.name, avatar: pickAvatar(profile.avatar) })
      return
    }
    setGuestName('')
    setGuestAvatar(0)
    setScreen('setup')
  }

  const finishGuestSetup = (event) => {
    event.preventDefault()
    const name = guestName.trim().slice(0, 18)
    if (!name) {
      setError('Type your name first')
      return
    }
    let guestId = localStorage.getItem('pinweelo-zombie-guest')
    if (!/^guest-[a-z0-9-]{8,80}$/i.test(String(guestId || ''))) {
      const random = window.crypto && window.crypto.randomUUID
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`
      guestId = `guest-${random}`
      localStorage.setItem('pinweelo-zombie-guest', guestId)
    }
    const avatar = pickAvatar(guestAvatar)
    localStorage.setItem('pinweelo-zombie-guest-profile', JSON.stringify({ name, avatar }))
    beginMatch({ id: guestId, pid: '', name, avatar })
  }

  useEffect(() => {
    if (screen !== 'game') return undefined
    const socket = getSocket()
    const onState = (next) => {
      stateReceivedRef.current = performance.now()
      const own = next && next.players && next.players.find((player) => player.id === playerId)
      if (own && own.hasGun && !hadGunRef.current && audioRef.current) audioRef.current.play('pickup')
      hadGunRef.current = Boolean(own && own.hasGun)
      setGame(next)
    }
    const onJoined = (data) => setPlayerId(data.playerId)
    const onError = (data) => setError(data.message || 'Game error')
    const onDenied = () => {
      if (audioRef.current) audioRef.current.play('door')
      setLocationNotice('The people inside did not open the door')
      window.setTimeout(() => setLocationNotice(''), 2800)
    }
    const onShot = (data) => {
      combatRef.current.shots.push({ ...data, receivedAt: Date.now() })
      if (audioRef.current) audioRef.current.play('shot', data.type)
    }
    const onBite = (data) => {
      combatRef.current.bites.push({ ...data, receivedAt: Date.now() })
      if (audioRef.current) audioRef.current.play('bite')
    }
    const onTurned = (data) => {
      combatRef.current.turns.push({ ...data, receivedAt: Date.now() })
      if (audioRef.current) audioRef.current.play('turn')
    }
    socket.on('zombie:state', onState)
    socket.on('zombie:joined', onJoined)
    socket.on('zombie:error', onError)
    socket.on('zombie:door-denied', onDenied)
    socket.on('zombie:shot', onShot)
    socket.on('zombie:bite', onBite)
    socket.on('zombie:turned', onTurned)
    return () => {
      socket.off('zombie:state', onState)
      socket.off('zombie:joined', onJoined)
      socket.off('zombie:error', onError)
      socket.off('zombie:door-denied', onDenied)
      socket.off('zombie:shot', onShot)
      socket.off('zombie:bite', onBite)
      socket.off('zombie:turned', onTurned)
    }
  }, [playerId, screen])

  useEffect(() => () => {
    if (audioRef.current) audioRef.current.stop()
    audioRef.current = null
  }, [])

  useEffect(() => {
    const own = game && game.players && game.players.find((p) => p.id === playerId)
    if (own && own.role === 'zombie') setPhoneOpen(false)
    const rooms = (game && game.radioRooms) || []
    if (radioTab !== 'all' && !rooms.some((room) => room.id === radioTab)) setRadioTab('all')
  }, [game, playerId, radioTab])

  useEffect(() => {
    const down = (event) => {
      if (/input|textarea/i.test((event.target && event.target.tagName) || '')) return
      if (['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'].includes(event.key.toLowerCase())) {
        keysRef.current.add(event.key.toLowerCase())
        event.preventDefault()
      }
      if (event.code === 'Space') {
        const me = stateRef.current && stateRef.current.players.find((p) => p.id === playerId)
        if (me && me.hasGun) getSocket().emit('zombie:shoot', { dx: aimRef.current.x, dy: aimRef.current.y })
      }
    }
    const up = (event) => keysRef.current.delete(event.key.toLowerCase())
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    return () => {
      window.removeEventListener('keydown', down)
      window.removeEventListener('keyup', up)
    }
  }, [playerId])

  useEffect(() => {
    if (screen !== 'game') return undefined
    const timer = setInterval(() => {
      const keys = keysRef.current
      let x = joyRef.current.x
      let y = joyRef.current.y
      if (keys.has('a') || keys.has('arrowleft')) x -= 1
      if (keys.has('d') || keys.has('arrowright')) x += 1
      if (keys.has('w') || keys.has('arrowup')) y -= 1
      if (keys.has('s') || keys.has('arrowdown')) y += 1
      const mag = Math.hypot(x, y)
      if (mag > 1) { x /= mag; y /= mag }
      if (mag > 0.08) aimRef.current = { x, y }
      getSocket().emit('zombie:input', { x, y, ax: aimRef.current.x, ay: aimRef.current.y })
    }, 50)
    return () => clearInterval(timer)
  }, [screen])

  const paint = useCallback(() => {
    const canvas = canvasRef.current
    const gameState = stateRef.current
    if (!canvas || !gameState) {
      frameRef.current = requestAnimationFrame(paint)
      return
    }
    const box = canvas.getBoundingClientRect()
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    if (canvas.width !== Math.round(box.width * dpr) || canvas.height !== Math.round(box.height * dpr)) {
      canvas.width = Math.round(box.width * dpr)
      canvas.height = Math.round(box.height * dpr)
    }
    const ctx = canvas.getContext('2d')
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    const liveIds = new Set()
    const renderedPlayers = gameState.players.map((p) => {
      liveIds.add(p.id)
      let targetX = p.x
      let targetY = p.y
      if (p.id === playerId && gameState.phase === 'playing' && p.stunnedUntil <= Date.now()) {
        let dx = Number(p.dx) || 0
        let dy = Number(p.dy) || 0
        const keys = keysRef.current
        const inputX = joyRef.current.x + (keys.has('d') || keys.has('arrowright') ? 1 : 0) - (keys.has('a') || keys.has('arrowleft') ? 1 : 0)
        const inputY = joyRef.current.y + (keys.has('s') || keys.has('arrowdown') ? 1 : 0) - (keys.has('w') || keys.has('arrowup') ? 1 : 0)
        const inputMag = Math.hypot(inputX, inputY)
        if (inputMag > 0.02) {
          dx = inputX / Math.max(1, inputMag)
          dy = inputY / Math.max(1, inputMag)
        }
        const age = Math.min(90, performance.now() - stateReceivedRef.current) / 1000
        const speed = p.role === 'zombie' ? 245 : 270
        targetX += dx * speed * age
        targetY += dy * speed * age
      }
      let shown = renderPlayersRef.current.get(p.id)
      if (!shown || shown.place !== p.place) {
        shown = { x: p.x, y: p.y, place: p.place }
        renderPlayersRef.current.set(p.id, shown)
      } else {
        const amount = p.id === playerId ? 0.34 : 0.24
        shown.x += (targetX - shown.x) * amount
        shown.y += (targetY - shown.y) * amount
      }
      return { ...p, x: shown.x, y: shown.y }
    })
    for (const id of renderPlayersRef.current.keys()) {
      if (!liveIds.has(id)) renderPlayersRef.current.delete(id)
    }
    const me = renderedPlayers.find((p) => p.id === playerId) || renderedPlayers[0]
    const inside = Boolean(me && me.place)
    const phone = box.width < 900
    const scale = phone
      ? Math.max(0.62, Math.min(0.88, Math.min(box.width / 540, box.height / 400)))
      : Math.max(0.55, Math.min(0.78, Math.min(box.width / 860, box.height / 620)))
    const worldX = inside ? me.outsideX : me && me.x
    const worldY = inside ? me.outsideY : me && me.y
    const camX = clampCam(worldX || WORLD_WIDTH / 2, box.width, scale, WORLD_WIDTH)
    const camY = clampCam(worldY || WORLD_HEIGHT / 2, box.height, scale, WORLD_HEIGHT)
    aimOriginRef.current = {
      x: box.width / 2 + ((me && me.x) - camX) * scale,
      y: box.height / 2 + ((me && me.y) - camY) * scale
    }
    ctx.clearRect(0, 0, box.width, box.height)
    ctx.save()
    ctx.translate(box.width / 2, box.height / 2)
    ctx.scale(scale, scale)
    ctx.translate(-camX, -camY)
    ctx.drawImage(cachedMap(), 0, 0)
    drawWorldAnimations(ctx, Date.now())
    for (const gun of gameState.guns || []) {
      ctx.save(); ctx.translate(gun.x, gun.y)
      ctx.shadowColor = '#f5d36a'; ctx.shadowBlur = 20
      const gunImage = weaponSprite(gun.type)
      if (gunImage) {
        ctx.drawImage(gunImage, -34, -25, 68, 52)
      } else {
        ctx.fillStyle = '#b8bec5'; ctx.fillRect(-26, -8, 52, 14)
        ctx.fillStyle = '#42342a'; ctx.beginPath(); ctx.moveTo(-10, 5); ctx.lineTo(9, 5); ctx.lineTo(4, 28); ctx.lineTo(-14, 24); ctx.closePath(); ctx.fill()
      }
      ctx.shadowBlur = 0
      const shots = gun.type === 'smg' ? 3 : gun.type === 'rifle' ? 2 : 1
      ctx.fillStyle = '#f5d36a'; ctx.font = '700 12px sans-serif'; ctx.textAlign = 'center'; ctx.fillText(`${shots} SHOT${shots === 1 ? '' : 'S'}`, 0, 38)
      ctx.fillStyle = 'rgba(244,232,194,.72)'; ctx.font = '700 10px sans-serif'
      ctx.fillText(String(gun.name || gun.type || 'weapon').toUpperCase(), 0, 53)
      ctx.restore()
    }
    const now = Date.now()
    for (const building of gameState.buildings || buildings) {
      ctx.save(); ctx.translate(building.x, building.y)
      ctx.fillStyle = building.breachEndsAt > now ? '#d43c31' : '#d0b56f'
      ctx.shadowColor = ctx.fillStyle; ctx.shadowBlur = building.breachEndsAt > now ? 24 : 10
      ctx.fillRect(-16, -10, 32, 27)
      ctx.shadowBlur = 0
      ctx.fillStyle = 'rgba(240,229,197,.8)'; ctx.font = '700 13px Georgia'; ctx.textAlign = 'center'
      ctx.fillText(building.name, 0, 39)
      if (building.occupants) ctx.fillText(`${building.occupants} inside`, 0, 56)
      ctx.restore()
    }
    for (const p of renderedPlayers) {
      if (!p.place) drawPlayer(ctx, p, p.id === playerId && !inside, now, combatRef.current)
    }
    drawCombatEffects(ctx, combatRef.current, now, '')
    if (!inside) drawWorldOcclusion(ctx, me)
    ctx.restore()
    const fog = ctx.createRadialGradient(box.width / 2, box.height / 2, Math.min(box.width, box.height) * 0.22, box.width / 2, box.height / 2, Math.max(box.width, box.height) * 0.74)
    fog.addColorStop(0, 'rgba(4,8,5,0)')
    fog.addColorStop(0.72, 'rgba(2,4,3,.32)')
    fog.addColorStop(1, 'rgba(0,0,0,.9)')
    ctx.fillStyle = fog
    ctx.fillRect(0, 0, box.width, box.height)
    if (inside) {
      const popupScale = Math.min((box.width - 36) / 900, (box.height - 150) / 900, 0.78)
      const popupSize = 900 * popupScale
      const left = (box.width - popupSize) / 2
      const top = (box.height - popupSize) / 2 + 12
      aimOriginRef.current = { x: left + me.x * popupScale, y: top + me.y * popupScale }
      ctx.save()
      ctx.shadowColor = 'rgba(0,0,0,.9)'
      ctx.shadowBlur = 45
      ctx.fillStyle = '#0b0c0a'
      ctx.fillRect(left - 9, top - 9, popupSize + 18, popupSize + 18)
      ctx.shadowBlur = 0
      ctx.strokeStyle = 'rgba(210,181,108,.5)'
      ctx.lineWidth = 2
      ctx.strokeRect(left - 9, top - 9, popupSize + 18, popupSize + 18)
      ctx.translate(left, top)
      ctx.scale(popupScale, popupScale)
      drawInterior(ctx, buildings.find((item) => item.id === me.place))
      for (const p of renderedPlayers) {
        if (p.place === me.place) drawPlayer(ctx, p, p.id === playerId, now, combatRef.current)
      }
      drawCombatEffects(ctx, combatRef.current, now, me.place)
      drawInteriorOcclusion(ctx, buildings.find((item) => item.id === me.place), me)
      ctx.restore()
    }
    frameRef.current = requestAnimationFrame(paint)
  }, [playerId])

  useEffect(() => {
    if (screen !== 'game') return undefined
    frameRef.current = requestAnimationFrame(paint)
    return () => cancelAnimationFrame(frameRef.current)
  }, [paint, screen])

  const aimAt = (event) => {
    const box = canvasRef.current.getBoundingClientRect()
    const x = event.clientX - box.left - aimOriginRef.current.x
    const y = event.clientY - box.top - aimOriginRef.current.y
    const mag = Math.hypot(x, y) || 1
    aimRef.current = { x: x / mag, y: y / mag }
  }

  const joyMove = (event) => {
    const box = event.currentTarget.getBoundingClientRect()
    const touch = event.touches ? event.touches[0] : event
    const x = touch.clientX - box.left - box.width / 2
    const y = touch.clientY - box.top - box.height / 2
    const mag = Math.max(1, Math.hypot(x, y))
    const power = Math.min(1, mag / (box.width * 0.34))
    joyRef.current = { x: (x / mag) * power, y: (y / mag) * power }
    setJoystick({ active: true, dx: joyRef.current.x * 34, dy: joyRef.current.y * 34 })
  }
  const joyStop = () => {
    joyRef.current = { x: 0, y: 0 }
    setJoystick({ active: false, dx: 0, dy: 0 })
  }
  const beginJoystick = (event) => {
    event.preventDefault()
    joyPointerRef.current = event.pointerId
    if (event.currentTarget.setPointerCapture && event.pointerId != null) {
      try { event.currentTarget.setPointerCapture(event.pointerId) } catch (err) {}
    }
    joyMove(event)
  }
  const moveJoystick = (event) => {
    if (joyPointerRef.current !== event.pointerId) return
    event.preventDefault()
    joyMove(event)
  }
  const endJoystick = (event) => {
    if (joyPointerRef.current !== event.pointerId) return
    event.preventDefault()
    joyPointerRef.current = null
    joyStop()
    getSocket().emit('zombie:input', { x: 0, y: 0, ax: aimRef.current.x, ay: aimRef.current.y })
  }

  const me = game && game.players.find((p) => p.id === playerId)
  const countdown = game && game.phase === 'lobby' ? Math.max(0, Math.ceil((game.lobbyEndsAt - clock) / 1000)) : 0
  const winner = game && game.winner
  const insideBuilding = me && buildings.find((item) => item.id === me.place)
  const nearBuilding = me && !me.place
    ? buildings.map((item) => ({ ...item, d: Math.hypot(me.x - item.x, me.y - item.y) })).sort((a, b) => a.d - b.d)[0]
    : null
  const atDoor = nearBuilding && nearBuilding.d <= 115 ? nearBuilding : null
  const myRequest = game && (game.entryRequests || []).find((item) => item.applicant === playerId)
  const requestsHere = insideBuilding
    ? (game.entryRequests || []).filter((item) => item.buildingId === insideBuilding.id)
    : []
  const chatRows = insideBuilding && game.placeChats ? (game.placeChats[insideBuilding.id] || []) : []
  const hordeAtDoor = atDoor && game
    ? game.players.filter((p) => p.role === 'zombie' && !p.place && Math.hypot(p.x - atDoor.x, p.y - atDoor.y) <= 105).length
    : 0
  const serverBuilding = insideBuilding && (game.buildings || []).find((item) => item.id === insideBuilding.id)

  const sendPlaceChat = (event) => {
    event.preventDefault()
    const text = chatText.trim()
    if (!text) return
    getSocket().emit('zombie:place-chat', { text })
    setChatText('')
  }

  const sendRadio = (event) => {
    event.preventDefault()
    const text = radioText.trim()
    if (!text) return
    getSocket().emit('zombie:radio-chat', { text, roomId: radioTab === 'all' ? '' : radioTab })
    setRadioText('')
  }

  const radioRooms = (game && game.radioRooms) || []
  const activeRoom = radioRooms.find((room) => room.id === radioTab)
  const radioRows = radioTab === 'all' ? ((game && game.humanChat) || []) : ((activeRoom && activeRoom.messages) || [])
  const radioPeople = ((game && game.radioPeople) || []).filter((person) => person.id !== playerId)


  return (
    <main className="zombiePage">
      <Helmet>
        <title>Zombie · Pinweelo</title>
        <meta name="theme-color" content="#070807" />
      </Helmet>

      {screen === 'intro' && (
        <section className="zombieIntro">
          <div className="zombieMoon" />
          <button className="zombieExit" type="button" onClick={() => navigate('/')}><FiLogOut /> Leave</button>
          <div className="zombieIntroCard">
            <p className="zombieKicker">PINWEELO HALLOWEEN EVENT</p>
            <h1>ZOMBIE</h1>
            <p className="zombieTagline">Fifty enter. One carries the hunger. Only the last survivor earns the mark.</p>
            <div className="zombieRules">
              <span><b>50</b> players</span>
              <span><b>10</b> seconds to turn</span>
              <span><b>5</b> rare weapons</span>
              <span><b>8</b> enterable places</span>
            </div>
            {error ? <p className="zombieError">{error}</p> : null}
            <button className="zombieStart" type="button" onClick={start}>START GAME</button>
            <small>WASD / arrows or touch joystick · Space or FIRE to shoot</small>
          </div>
        </section>
      )}

      {screen === 'setup' && (
        <section className="zombieIntro">
          <div className="zombieMoon" />
          <button className="zombieExit" type="button" onClick={() => setScreen('intro')}><FiLogOut /> Back</button>
          <form className="zombieIntroCard zombieSetup" onSubmit={finishGuestSetup}>
            <p className="zombieKicker">NEW SURVIVOR</p>
            <h1>WHO ARE YOU?</h1>
            <p className="zombieTagline">Unregistered players pick a name and a face before they enter the cursed grounds.</p>
            <label className="zombieSetupName">
              Your name
              <input value={guestName} onChange={(event) => setGuestName(event.target.value)} maxLength={18} placeholder="Type a name" autoFocus />
            </label>
            <p className="zombieSetupHint">Choose an avatar</p>
            <div className="zombieAvatarGrid">
              {Array.from({ length: 20 }, (_, index) => (
                <button
                  key={index}
                  type="button"
                  className={`zombieAvatarPick${guestAvatar === index ? ' isOn' : ''}`}
                  style={{
                    backgroundImage: 'url(/zombie/human-roster.jpg)',
                    backgroundPosition: `${(index % 10) * (100 / 9)}% ${Math.floor(index / 10) * 100}%`
                  }}
                  onClick={() => setGuestAvatar(index)}
                  aria-label={`Avatar ${index + 1}`}
                />
              ))}
            </div>
            {error ? <p className="zombieError">{error}</p> : null}
            <button className="zombieStart" type="submit">ENTER THE GAME</button>
          </form>
        </section>
      )}

      {screen === 'queue' && (
        <section className="zombieQueue">
          <div className="zombieLoader" />
          <h1>Entering the cursed grounds…</h1>
          <p>Finding survivors</p>
        </section>
      )}

      {screen === 'game' && game && (
        <section className="zombieGame">
          <canvas ref={canvasRef} onPointerMove={aimAt} />
          <header className="zombieHudTop">
            <button type="button" onClick={() => navigate('/')} aria-label="Leave game"><FiLogOut /></button>
            <div className="zombieScore">
              <span className="humans"><i /> {game.humans} humans</span>
              <strong>{game.phase === 'lobby' ? `Starts in ${countdown}` : fmt(clock - game.startedAt)}</strong>
              <span className="zombies"><i /> {game.zombies} zombies</span>
            </div>
            <button
              className="zombieSound"
              type="button"
              aria-label={muted ? 'Turn sound on' : 'Mute sound'}
              onClick={() => {
                const next = !muted
                setMuted(next)
                if (audioRef.current) audioRef.current.setMuted(next)
              }}
            >
              {muted ? <FiVolumeX /> : <FiVolume2 />}
            </button>
          </header>

          {me && me.role !== 'zombie' && (
            <button
              className={`zombiePhoneBtn${phoneOpen ? ' isOn' : ''}`}
              type="button"
              aria-label="Open survivor radio"
              onClick={() => setPhoneOpen((open) => !open)}
            >
              <FiPhone />
            </button>
          )}

          {me && me.role !== 'zombie' && phoneOpen && (
            <aside className="zombiePhone">
              <div className="zombieChatHead">
                <h3>Survivor radio</h3>
                <button type="button" onClick={() => setPhoneOpen(false)}><FiX /></button>
              </div>
              <div className="zombieRadioTabs">
                <button type="button" className={radioTab === 'all' ? 'isOn' : ''} onClick={() => setRadioTab('all')}>All humans</button>
                {radioRooms.map((room) => (
                  <button key={room.id} type="button" className={radioTab === room.id ? 'isOn' : ''} onClick={() => setRadioTab(room.id)}>{room.name}</button>
                ))}
              </div>
              <div className="zombiePlaceChat zombieRadioLog">
                {radioRows.length === 0 ? <p className="zombieChatEmpty">Keep this off zombie ears.</p> : null}
                {radioRows.slice(-12).map((row) => <p key={row.id}>{row.system ? row.text : <><b>{row.name}:</b> {row.text}</>}</p>)}
              </div>
              <form onSubmit={sendRadio}>
                <input value={radioText} onChange={(event) => setRadioText(event.target.value)} maxLength={160} placeholder={radioTab === 'all' ? 'Message every human…' : 'Message this room…'} />
                <button type="submit">Send</button>
              </form>
              <form
                className="zombieRadioCreate"
                onSubmit={(event) => {
                  event.preventDefault()
                  const name = roomName.trim()
                  if (!name) return
                  getSocket().emit('zombie:radio-create', { name }, (reply) => {
                    if (reply && reply.id) setRadioTab(reply.id)
                  })
                  setRoomName('')
                }}
              >
                <input value={roomName} onChange={(event) => setRoomName(event.target.value)} maxLength={22} placeholder="Create a private room" />
                <button type="submit">Create</button>
              </form>
              {activeRoom && (
                <div className="zombieRadioInvite">
                  <p>Invite a human into {activeRoom.name}</p>
                  {radioPeople.filter((person) => !(activeRoom.members || []).some((member) => member.id === person.id)).map((person) => (
                    <button key={person.id} type="button" onClick={() => getSocket().emit('zombie:radio-invite', { roomId: activeRoom.id, playerId: person.id })}>
                      {person.name}
                    </button>
                  ))}
                </div>
              )}
            </aside>
          )}

          {me && (
            <aside className={`zombieRole is-${me.role}`}>
              <b>{me.role === 'zombie' ? 'YOU ARE THE ZOMBIE' : me.role === 'turning' ? 'YOU WERE BITTEN' : 'SURVIVE'}</b>
              <span>{me.role === 'zombie' ? `${me.bites} bitten` : me.role === 'turning' ? `Turning in ${Math.max(0, Math.ceil((me.turningAt - clock) / 1000))}s · only zombies know` : me.hasGun ? `${String(me.gunType || 'weapon').toUpperCase()} · ${me.gunAmmo || 1} shot${(me.gunAmmo || 1) === 1 ? '' : 's'}` : 'Find a weapon or keep running'}</span>
            </aside>
          )}

          <aside className="zombieFeed">
            {(game.feed || []).slice(-4).map((item) => <p key={item.id}>{item.text}</p>)}
          </aside>

          {locationNotice && <div className="zombieLocationToast">{locationNotice}</div>}

          {atDoor && game.phase === 'playing' && (
            <div className="zombieDoorPrompt">
              <b>{atDoor.name}</b>
              {me && me.role === 'zombie' ? (
                <span>{hordeAtDoor >= 5 ? 'Breaking the door…' : `${hordeAtDoor}/5 zombies at the door`}</span>
              ) : myRequest ? (
                <span>Waiting for votes · {myRequest.yes}/2 approved</span>
              ) : (
                <button type="button" onClick={() => getSocket().emit('zombie:enter', { buildingId: atDoor.id })}>ASK TO ENTER</button>
              )}
            </div>
          )}

          {insideBuilding && (
            <>
              <button className="zombieLeavePlace" type="button" onClick={() => getSocket().emit('zombie:exit')}>EXIT {insideBuilding.name}</button>
              {serverBuilding && serverBuilding.breachEndsAt > clock && (
                <div className="zombieBreachAlert">ZOMBIES AT THE DOOR · BREACH IN {Math.max(0, Math.ceil((serverBuilding.breachEndsAt - clock) / 1000))}</div>
              )}
              <button className={`zombieChatToggle${requestsHere.length ? ' hasAlert' : ''}`} type="button" onClick={() => setChatOpen((open) => !open)}>
                <FiMessageCircle /> {chatOpen ? 'CLOSE CHAT' : `CHAT · ${chatRows.length}`}{requestsHere.length ? ` · ${requestsHere.length} AT DOOR` : ''}
              </button>
              {chatOpen && <aside className="zombiePlacePanel">
                <div className="zombieChatHead"><h3>{insideBuilding.name}</h3><button type="button" onClick={() => setChatOpen(false)}><FiX /></button></div>
                {requestsHere.map((request) => (
                  <div className="zombieVote" key={request.id}>
                    <p><b>{request.name}</b> wants to come inside</p>
                    <span>{request.yes}/2 approvals</span>
                    <div>
                      <button type="button" onClick={() => getSocket().emit('zombie:door-vote', { applicant: request.applicant, accept: true })}>Let in</button>
                      <button type="button" onClick={() => getSocket().emit('zombie:door-vote', { applicant: request.applicant, accept: false })}>No</button>
                    </div>
                  </div>
                ))}
                <div className="zombiePlaceChat">
                  {chatRows.length === 0 ? <p className="zombieChatEmpty">The room is quiet…</p> : null}
                  {chatRows.slice(-6).map((row) => <p key={row.id}><b>{row.name}:</b> {row.text}</p>)}
                </div>
                <form onSubmit={sendPlaceChat}>
                  <input value={chatText} onChange={(event) => setChatText(event.target.value)} maxLength={160} placeholder="Talk to everyone inside…" />
                  <button type="submit">Send</button>
                </form>
              </aside>}
            </>
          )}

          {game.phase === 'lobby' && (
            <div className="zombieLobby">
              <strong>{countdown}</strong>
              <p>{game.players.filter((p) => !p.npc).length} real player{game.players.filter((p) => !p.npc).length === 1 ? '' : 's'} ready</p>
              <small>Empty places become NPC survivors</small>
              <small className="zombieMovementWait">Movement unlocks when the countdown ends</small>
            </div>
          )}

          <div
            className="zombieJoystick"
            role="application"
            aria-label="Movement joystick. Drag in any direction."
            tabIndex={0}
            onPointerDown={beginJoystick}
            onPointerMove={moveJoystick}
            onPointerUp={endJoystick}
            onPointerCancel={endJoystick}
          >
            <i style={{ transform: `translate(${joystick.dx}px, ${joystick.dy}px)` }} />
          </div>

          <button
            type="button"
            className={`zombieFire${me && me.hasGun ? ' isReady' : ''}`}
            disabled={!me || !me.hasGun}
            onClick={() => getSocket().emit('zombie:shoot', { dx: aimRef.current.x, dy: aimRef.current.y })}
          >
            <FiCrosshair />
            <span>{me && me.hasGun ? `${String(me.gunType || 'FIRE').toUpperCase()} ${me.gunAmmo || 1}` : 'EMPTY'}</span>
          </button>

          {game.phase === 'ended' && (
            <div className="zombieEnd">
              <p>THE CURSE HAS WON</p>
              <h2>{winner ? winner.name : 'No one'} lasted the longest</h2>
              <strong>{winner ? fmt(winner.survivalMs) : '0:00'}</strong>
              {winner && winner.id === playerId ? <em>Halloween Survivor badge added to your profile</em> : null}
              <button type="button" onClick={() => { setGame(null); setScreen('intro') }}>PLAY AGAIN</button>
            </div>
          )}
        </section>
      )}
    </main>
  )
}

const clampCam = (position, screen, scale, worldSize) => {
  const half = screen / (2 * scale)
  if (half * 2 >= worldSize) return worldSize / 2
  return Math.max(half, Math.min(worldSize - half, position))
}

export default Zombie
